/**
 * Service API HTTP pour appeler les Cloud Functions Express
 * Remplace httpsCallable par des appels HTTP directs avec authentification
 */

import { auth } from "./config";

// URL de base de l'API (Cloud Function Express)
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  "https://europe-west1-natura-beldi-glovo-management.cloudfunctions.net/api";

/**
 * Récupère le token Firebase de l'utilisateur authentifié
 * Force le refresh si le token expire dans moins de 5 minutes
 * Attend que Firebase Auth soit prêt si nécessaire
 */
async function getAuthToken(forceRefresh = false): Promise<string | null> {
  // Si auth.currentUser est null, attendre que Firebase Auth soit prêt
  let user = auth.currentUser;

  if (!user) {
    // Attendre jusqu'à 3 secondes que Firebase Auth restaure la session
    user = await new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 3000);
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(authUser);
      });
    });
  }

  if (!user) {
    return null;
  }

  try {
    // ✅ Force refresh pour éviter les tokens expirés
    return await user.getIdToken(forceRefresh);
  } catch (error: any) {
    console.error("Error getting auth token:", error);

    // 09/10/2026 — distinguer « le reseau a hoquete » de « ce compte n'a
    // plus le droit d'etre la ».
    //
    // Depuis que le role et le magasin voyagent dans le jeton, desactiver
    // un compte ou changer son role revoque ses sessions cote serveur. Le
    // rafraichissement echoue alors pour de bon, et laisser l'ecran
    // reessayer indefiniment n'aide personne : il affiche des erreurs sans
    // jamais dire quoi faire.
    //
    // On ne deconnecte QUE sur ces codes-la. Une coupure reseau rend la
    // meme exception a ce niveau, et mettre tout le monde dehors a chaque
    // perte de connexion serait pire que le probleme.
    const codesDeRevocation = [
      "auth/user-token-expired",
      "auth/user-disabled",
      "auth/invalid-user-token",
      "auth/requires-recent-login",
    ];

    if (codesDeRevocation.includes(error?.code)) {
      console.warn("Session revoquee : deconnexion.", error?.code);
      try {
        const { signOut } = await import("firebase/auth");
        await signOut(auth);
      } catch {
        /* Deja deconnecte, ou Firebase indisponible : rien a rattraper. */
      }
    }

    return null;
  }
}

/**
 * Effectue une requête HTTP authentifiée vers l'API
 * Avec retry automatique en cas de 401 (token expiré)
 */
async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getAuthToken();

  if (!token) {
    throw new Error("User not authenticated");
  }

  const url = `${API_BASE_URL}${endpoint}`;

  let response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  // ✅ Si 401, rafraîchir le token et réessayer UNE fois
  if (response.status === 401) {
    console.log("Token expired, refreshing...");
    const newToken = await getAuthToken(true); // Force refresh

    if (newToken) {
      response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${newToken}`,
          ...options.headers,
        },
      });
    }
  }

  if (!response.ok) {
    // « Unknown error » ne disait rien a personne : la reponse n'etait pas
    // du JSON (coupure reseau, delai depasse, trop de requetes) et on jetait
    // tout ce qu'elle contenait. On lit desormais le texte brut, et on dit
    // au moins CE QUI s'est passe.
    const brut = await response.text().catch(() => "");
    let message = "";
    try {
      message = JSON.parse(brut)?.message || "";
    } catch {
      /* Pas du JSON : on retombe sur le statut, lisible en francais. */
    }
    if (!message) {
      message =
        response.status === 429
          ? "Trop de demandes en même temps. Patientez quelques secondes et réessayez."
          : response.status >= 502 && response.status <= 504
            ? "Le serveur n'a pas répondu à temps. Vérifiez d'abord si l'action a été faite avant de réessayer."
            : response.status === 0 || !response.status
              ? "Connexion interrompue. Vérifiez le réseau et réessayez."
              : `Erreur ${response.status}${brut ? ` — ${brut.slice(0, 120)}` : ""}`;
    }
    throw new Error(message);
  }

  return response.json();
}

/* ============================================================================
 *  Import de l'export Glovo — formes échangées avec l'API
 * ========================================================================== */

export interface ChangementGlovo {
  champ:
    | "montant"
    | "statut"
    | "preparation"
    | "articles"
    | "deduction"
    | "remboursement";
  avant: string;
  apres: string;
}

export interface LigneRapportGlovo {
  numero: string;
  orderId: string;
  magasin: string;
  statutGlovo: string;
  changements: ChangementGlovo[];
  articlesRetires: { nom: string; quantite: number; prixCentimes: number }[];
  ecartCentimes: number;
}

export interface RapportImportGlovo {
  rappel?: string;
  fichier: {
    commandesLues: number;
    echecs: { ligne: number; raison: string }[];
    colonnesManquantes: string[];
    periode: { du: string | null; au: string | null };
  };
  resume: {
    dejaAJour: number;
    aModifier: number;
    absentesDeLaBase: number;
    conflits: number;
    /** Livrees chez Glovo, absentes ici (absent sur un ancien serveur). */
    absentesLivrees?: number;
    absentesLivreesCentimes?: number;
    /** Livrees, absentes, non traitees : l'import est bloque tant qu'il en reste. */
    absentesABloquer?: number;
  };
  aModifier: LigneRapportGlovo[];
  absentesDeLaBase: {
    numero: string;
    magasin: string;
    recueA: string | null;
    montant: string;
    /** Libelle Glovo : « Livrée », « Annulée »… (absent sur un ancien serveur). */
    statut?: string;
    sousTotalCentimes?: number;
    livree?: boolean;
    vue?: { par: string; le: string; note: string } | null;
  }[];
  conflits: { numero: string; raison: string }[];
  dejaAJour: string[];
}

/** Une commande perdue a la reception, vue (ou recreee) depuis le fichier. */
export interface ApercuCommandeAbsente {
  numero: string;
  magasin: string;
  storeId: string | null;
  statutGlovo: string;
  recueA: string | null;
  sousTotalCentimes: number;
  articles: { nom: string; quantite: number; prixCentimes: number; trouve: boolean }[];
  totalArticlesCentimes: number;
  ecartCentimes: number;
  possible: boolean;
  raisons: string[];
  orderId?: string;
}

export interface ResultatImportGlovo {
  message: string;
  resultat: {
    modifiees: number;
    echouees: { numero: string; raison: string }[];
    ecartTotalCentimes: number;
    journalId: string | null;
  };
  resume: RapportImportGlovo["resume"];
  absentesDeLaBase: RapportImportGlovo["absentesDeLaBase"];
  conflits: { numero: string; raison: string }[];
}

export interface LigneJournalGlovo {
  id: string;
  quand: string | null;
  parQui: string;
  periode: { du: string | null; au: string | null };
  commandesLues: number;
  modifiees: number;
  dejaAJour: number;
  absentes: number;
  conflits: number;
  echouees: number;
  ecartTotal: string;
}

/** Le detail d'un import Glovo : les commandes qui font l'ecart. */
export interface DetailImportGlovo {
  id: string;
  quand: string | null;
  periode: { du: string | null; au: string | null } | null;
  ecartTotal: number;
  lignes: {
    numero: string;
    orderId: string | null;
    magasin: string | null;
    ecart: number;
    changements: { champ: string; avant: any; apres: any }[];
    statutGlovo: string | null;
    articlesRetires: { nom: string; quantite: number; prix: number }[];
    nonExplique: number;
    identificationImpossible: boolean;
    remboursement: number;
    raisonAnnulation: string | null;
  }[];
}

/**
 * Service API avec toutes les méthodes
 */
export interface ArticleCommande {
  id: string;
  nom: string;
  quantite: number;
  prixCentimes: number;
  /** Le fournisseur du panier de cet article (10/10/2026). */
  fournisseur?: string | null;
}

export interface QualificationArticle {
  articleId: string;
  article: string;
  motif: string;
  /**
   * Combien d'unites de cet article ont ete remboursees.
   *
   * Glovo rend parfois une seule unite sur deux commandees : sans ce
   * nombre, la retenue etait imputee sur l'article entier.
   */
  quantite: number;
  /** MAGASIN : confusion de commandes. FOURNISSEUR : erreur a la livraison. */
  imputation: "MAGASIN" | "FOURNISSEUR" | null;
}

export interface LigneRemboursement {
  id: string;
  numero: string;
  montantCentimes: number;
  recueA: string | null;
  motif: string | null;
  /** Un motif par article qualifie, avec qui porte la perte. */
  qualifications: QualificationArticle[];
  nbJustificatifs: number;
  /** Nombre de photos par article (articleId -> nombre), depuis le 10/10/2026. */
  photosParArticle?: Record<string, number>;
  articles: ArticleCommande[];
  /**
   * Le moment ou la ligne se ferme — 24 h apres l'heure Glovo.
   *
   * `null` quand on n'a pas su dater la ligne : elle reste alors
   * modifiable. Verrouiller sur une date qu'on n'a pas su lire bloquerait
   * une correction legitime sans pouvoir expliquer pourquoi.
   */
  verrouilleLe: string | null;
  /** Vrai une fois la ligne validee : seul l'admin peut encore ecrire. */
  verrouille: boolean;
  /** Validee (bouton « Valider », ou ancien delai pour les lignes d'avant le 24/10). */
  valide?: boolean;
  /** Motif(s) pose(s), pas encore valide : le bouton « Valider » s'affiche. */
  aValider?: boolean;
  validePar?: string | null;
  valideLe?: string | null;
  raisonNonValide?: string | null;
  /** La quinzaine du remboursement (« 2026-10-Q2 ») et si elle est cloturee (01/11/2026). */
  periode?: string | null;
  periodeCloturee?: boolean;
}

export interface JustificatifPhoto {
  id: string;
  photo: string;
  motif: string | null;
  ajouteLe: string;
  ajoutePar: string | null;
}

/** Une demande de retrait d'article (voir retraits-commande.handler). */
export interface RetraitCommande {
  id: string;
  orderId: string;
  orderCode: string | null;
  storeId: string | null;
  source: string | null;
  article: string;
  quantite: number;
  montantCentimes: number;
  motif: string | null;
  statut: "EN_ATTENTE" | "EN_COURS" | "RETIRE_GLOVO" | "A_RETIRER_TABLETTE" | "REFUSE";
  demandeParNom: string;
  demandeLe: string | null;
  decideParNom: string | null;
  decideLe: string | null;
  retraitApiPossible: boolean;
  glovo: any;
}

export const api = {
  // ============ FOURNISSEURS ============
  /**
   * Les emballages confies aux fournisseurs.
   *
   * `etat` renvoie, par fournisseur et par type : stock initial, remis sur
   * la periode, consomme, et ce qui reste. Sans dates, c'est la quinzaine en
   * cours — celle qu'on vient regarder en arrivant.
   */
  emballages: {
    // `frais` : relire sans la memoire du serveur, apres une saisie.
    etat: (debut?: string, fin?: string, frais?: boolean) => {
      const p = new URLSearchParams();
      if (debut) p.append("debut", debut);
      if (fin) p.append("fin", fin);
      if (frais) p.append("frais", "1");
      const q = p.toString();
      return apiRequest<any>(`/api/emballages/etat${q ? `?${q}` : ""}`);
    },
    remise: (data: {
      fournisseurId: string;
      emballage: string;
      quantite: number;
      /** Prix d'achat d'UNE piece, en dirhams. Le serveur convertit. */
      prixUnitaire?: number;
      le?: string;
      note?: string;
    }) =>
      apiRequest<any>("/api/emballages/remise", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    modifierRemise: (
      id: string,
      corps: {
        fournisseurId?: string;
        emballage?: string;
        quantite?: number;
        prixUnitaire?: number;
        le?: string;
        note?: string;
      },
    ) =>
      apiRequest<any>(`/api/emballages/remise/${id}`, {
        method: "PUT",
        body: JSON.stringify(corps),
      }),

    supprimerRemise: (id: string) =>
      apiRequest<any>(`/api/emballages/remise/${id}`, { method: "DELETE" }),

    /**
     * Le bon de livraison du fabricant : son numéro, sa date, le magasin qui
     * reçoit, et la ventilation de chaque ligne vers les fournisseurs.
     * Ce qui n'est ventilé par personne reste au responsable commande.
     */
    bon: (data: {
      numeroFacture: string;
      le: string;
      storeId: string;
      fabricant?: string;
      note?: string;
      lignes: Array<{
        emballage: string;
        quantite: number;
        /** Prix d'UNE pièce, en dirhams. Le serveur convertit. */
        prixUnitaire?: number;
        ventilation?: Array<{ fournisseurId: string; quantite: number }>;
      }>;
    }) =>
      apiRequest<{ id: string; success: boolean; totalCentimes: number }>(
        "/api/emballages/bon",
        { method: "POST", body: JSON.stringify(data) },
      ),

    /**
     * « Je n'arrive pas à équilibrer cette facture. » Amina prévient la
     * direction : le bouton gris, seul, serait un cul-de-sac.
     */
    signalerBlocage: (data: {
      numeroFacture: string;
      storeId: string;
      ecarts: Array<{ emballage: string; livre: number; affecte: number }>;
    }) =>
      apiRequest<{ success: boolean }>("/api/emballages/bon/blocage", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    bons: (storeId?: string, debut?: string, fin?: string) => {
      const p = new URLSearchParams();
      if (storeId) p.append("storeId", storeId);
      if (debut) p.append("debut", debut);
      if (fin) p.append("fin", fin);
      const q = p.toString();
      return apiRequest<{ bons: any[] }>(
        `/api/emballages/bons${q ? `?${q}` : ""}`,
      );
    },
  },

  fournisseurs: {
    getAll: async (filters?: { storeId?: string; category?: string }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      if (filters?.category) params.append("category", filters.category);
      const query = params.toString();
      const result = await apiRequest<{ fournisseurs: any[] }>(
        `/api/fournisseurs${query ? `?${query}` : ""}`,
      );
      return result.fournisseurs;
    },
    getById: async (id: string) => {
      const result = await apiRequest<{ fournisseur: any }>(
        `/api/fournisseurs/${id}`,
      );
      return result.fournisseur;
    },
    create: (data: any) =>
      apiRequest<any>("/api/fournisseurs", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      apiRequest<any>(`/api/fournisseurs/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiRequest<void>(`/api/fournisseurs/${id}`, {
        method: "DELETE",
      }),
    /** Nom de facturation et numero de compte de chaque frs (direction seule, 04/11/2026). */
    facturation: () =>
      apiRequest<{ facturation: Record<string, { nom: string | null; rib: string | null }> }>(
        "/api/fournisseurs/facturation",
      ),
    enregistrerFacturation: (id: string, corps: { nom: string; rib: string }) =>
      apiRequest<{ nom: string | null; rib: string | null }>(`/api/fournisseurs/${id}/facturation`, {
        method: "PUT",
        body: JSON.stringify(corps),
      }),

    /**
     * Les quatre chiffres de l'en-tete du fournisseur connecte.
     *
     * Tout est calcule par le serveur a chaque appel : rien n'est stocke.
     * La collection `stats` que cet ecran lisait avant n'etait plus
     * alimentee par personne, et affichait quatre zeros depuis des mois.
     */
    monTableauDeBord: () =>
      apiRequest<{
        produits: { actifs: number; desactives: number; total: number };
        magasin: {
          nom: string | null;
          ouvert: boolean;
          heureOuverture: number;
          heureFermeture: number;
          /** Jours de fermeture, format JavaScript : 0 = dimanche. */
          joursFermes: number[];
        };
        /** Ses horaires a lui, sept entrees, modifiables. */
        mesHoraires: Array<{
          jour: number;
          ferme: boolean;
          debut: number;
          fin: number;
        }>;
        du: { montant: number; articlesLivres: number };
        /**
         * En CENTIMES, comme tout l'argent stocke cote serveur.
         *
         * La division par cent se fait a l'affichage, a UN seul endroit.
         * C'est un facteur 100 recopie a plusieurs endroits qui avait
         * fausse tous les achats caisse.
         */
        ventesPerdues: { centimes: number; articles: number };
        remboursements: { centimes: number; nombre: number };
        preparation: { minutes: number | null; surCombien: number };
      }>("/api/fournisseurs/mon-tableau-de-bord"),

    /**
     * Ce qui lui a ete retenu, avec la photo.
     *
     * Le serveur part de SA fiche, jamais d'un identifiant envoye d'ici :
     * un fournisseur ne doit pas pouvoir lire les retenues d'un autre.
     */
    /**
     * Ce que les fournisseurs ont change eux-memes (horaires, conges,
     * service), avec qui a valide.
     */
    demandes: (storeId?: string) =>
      apiRequest<{
        demandes: Array<{
          id: string;
          nom: string;
          storeId: string | null;
          type: string;
          resume: string;
          /** Ce qui a vraiment ete paye en caisse, en centimes. */
          payeCentimes: number | null;
          /**
           * A trancher tout de suite : une fermeture de service en plein
           * horaire de travail. Ces lignes clignotent et sonnent.
           */
          urgent?: boolean;
          valide: boolean;
          validePar: string | null;
          valideLe: string | null;
          creeLe: string | null;
        }>;
        aVoir: number;
      }>(
        `/api/fournisseurs/demandes${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`,
      ),

    /** Les fournisseurs d'un magasin et leur derniere presence. */
    /** « Il est ouvert » : remet ses articles en vente (Glovo, Kooul, site). */
    ouvrirService: (fournisseurId: string) =>
      apiRequest<{ success: boolean; envoyes: number; glovoOk: boolean; erreur: string | null; parNom: string }>(
        `/api/fournisseurs/${fournisseurId}/ouvrir-service`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    presence: (storeId?: string) =>
      apiRequest<{
        storeId: string | null;
        fournisseurs: Array<{
          id: string;
          nom: string;
          vuLe: string | null;
          enService: boolean;
        }>;
      }>(
        `/api/fournisseurs/presence${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`,
      ),

    /** Remplit les compteurs du tableau depuis 30 jours d'historique (admin, une fois). */
    kpiRattrapage: () =>
      apiRequest<{ success: boolean; paniers: number; articles: number; fournisseurs: number; magasins: number }>(
        "/api/fournisseurs/kpi/rattrapage",
        { method: "POST" },
      ),

    /** Le tableau de performance des fournisseurs (admin), sur 1, 7 ou 30 jours. */
    kpi: (jours: 1 | 7 | 30) =>
      apiRequest<{
        periode: { jours: number; depuis: string };
        catalogueLe: string | null;
        lignes: Array<{
          fournisseurId: string;
          /** « Achats caisse » : la ligne n'est pas un compte. */
          nom?: string;
          caisse?: boolean;
          userId: string;
          storeId: string | null;
          articles: number;
          articlesActifs: number;
          pctActifs: number | null;
          paniersPrepares: number;
          prepMoyenneMin: number | null;
          caCentimes: number;
          articlesLivres: number;
          ventesPerduesCentimes: number;
          articlesPerdus: number;
          remboursementsCentimes: number;
          remboursements: number;
        }>;
      }>(`/api/fournisseurs/kpi?jours=${jours}`),

    validerDemande: (id: string, decision: "RETIRER" | "CAISSE" = "RETIRER") =>
      apiRequest<{ success: boolean; validePar: string | null; applique?: string | null }>(
        `/api/fournisseurs/demandes/${id}/valider`,
        { method: "PUT", body: JSON.stringify({ decision }) },
      ),

    /**
     * Le fournisseur s'est trompe : l'article n'est pas en rupture.
     * Rien ne s'eteint, l'article repasse en vente.
     */
    annulerDemande: (id: string) =>
      apiRequest<{ success: boolean; validePar: string | null; applique?: string | null }>(
        `/api/fournisseurs/demandes/${id}/annuler`,
        { method: "PUT" },
      ),


    /** Vente au poids (volaille) : le prix du jour au kilo. Voir vente-au-poids.ts. */
    prixDuJour: () =>
      apiRequest<{
        auPoids: boolean;
        aJour?: boolean;
        manquants?: number;
        jour?: string;
        prix?: Record<string, number>;
        prenom?: string;
        articles?: Array<{
          id: string;
          nom: string;
          nomAr: string | null;
          photo: string | null;
          /** Prix du marche dont il decoule (roumi, beldi), sinon null. */
          ref: string | null;
          refNom: string | null;
          refNomAr: string | null;
          prixKg: number | null;
          dernier: number | null;
        }>;
        /** Les prix du marche : poulet beldi, roumi, habbachi. */
        references?: Array<{
          id: string;
          nom: string;
          nomAr: string;
          photo?: string | null;
          prixKg: number | null;
          dernier: number | null;
          articles: string[];
        }>;
      }>("/api/fournisseurs/prix-du-jour"),

    enregistrerPrixDuJour: (prix: Record<string, number>) =>
      apiRequest<{ ok: boolean; jour: string; prix: Record<string, number> }>(
        "/api/fournisseurs/prix-du-jour",
        { method: "POST", body: JSON.stringify({ prix }) },
      ),

    /** Bureau : la ligne « Volaille — prix du jour » de la Comptabilite. */
    prixVolaille: (storeId: string) =>
      apiRequest<{
        jour: string;
        fournisseurs: Array<{
          fournisseurId: string;
          nom: string;
          jour: string;
          aJour: boolean;
          saisi: boolean;
          manquants: number;
          saisiLe: string | null;
          lignes: Array<{
            cle: string;
            article: string;
            calculeDepuis: string | null;
            prixKg: number | null;
            prixVeille: number | null;
            ecart: number | null;
          }>;
          corrections: Array<{
            cle: string;
            article: string;
            avant: number | null;
            apres: number;
            par: string | null;
            le: string | null;
          }>;
        }>;
      }>(`/api/fournisseurs/prix-volaille?storeId=${encodeURIComponent(storeId)}`),

    /** Bureau : corriger un prix du jour (trace gardee, pesees recalculees). */
    corrigerPrixVolaille: (fournisseurId: string, cle: string, prixKg: number) =>
      apiRequest<{ success: boolean; recalculees: number; avant: number | null; apres: number }>(
        "/api/fournisseurs/prix-volaille",
        { method: "PUT", body: JSON.stringify({ fournisseurId, cle, prixKg }) },
      ),

    /** Bureau : l'historique des prix du jour, en Excel (90 jours). */
    telechargerPrixVolaille: async (storeId: string, nomMagasin: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Session expirée, reconnecte-toi.");
      const reponse = await fetch(
        `${API_BASE_URL}/api/fournisseurs/prix-volaille/historique?storeId=${encodeURIComponent(storeId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!reponse.ok) throw new Error((await reponse.text()) || "Export impossible.");
      const blob = await reponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prix-volaille-${nomMagasin.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

    mesVentesPerdues: () =>
      apiRequest<{
        ventesPerdues: Array<{
          id: string;
          article: string;
          quantite: number;
          montantCentimes: number;
          orderId: string | null;
          orderCode: string | null;
          le: string | null;
        }>;
        totalCentimes: number;
        totalArticles: number;
        totalCommandes: number;
      }>("/api/fournisseurs/mes-ventes-perdues"),

    mesRemboursements: () =>
      apiRequest<{
        remboursements: Array<{
          id: string;
          montantCentimes: number;
          motif: string | null;
          article: string | null;
          orderCode: string | null;
          photoUrl: string | null;
          le: string | null;
        }>;
        totalCentimes?: number;
        /** Debut de la periode comptable couverte (1er ou 16 du mois). */
        periodeDebut?: string;
      }>("/api/fournisseurs/mes-remboursements"),
  },

  // ============ COLLABORATEURS ============
  collaborateurs: {
    getAll: async (filters?: { storeId?: string }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      const query = params.toString();
      const result = await apiRequest<{ collaborateurs: any[] }>(
        `/api/collaborateurs${query ? `?${query}` : ""}`,
      );
      return result.collaborateurs;
    },
    getById: async (id: string) => {
      const result = await apiRequest<{ collaborateur: any }>(
        `/api/collaborateurs/${id}`,
      );
      return result.collaborateur;
    },
    create: (data: any) =>
      apiRequest<any>("/api/collaborateurs", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      apiRequest<any>(`/api/collaborateurs/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiRequest<void>(`/api/collaborateurs/${id}`, {
        method: "DELETE",
      }),
  },

  // ============ STORES ============
  stores: {
    /** Les magasins Glovo : ouverts selon leurs horaires, et leur derniere commande. */
    etat: () =>
      apiRequest<{
        maintenant: string;
        magasins: Array<{
          id: string;
          nom: string;
          glovoStoreId: string;
          actif: boolean;
          heureOuverture: number;
          heureFermeture: number;
          jourFerme: boolean;
          ouvertSelonHoraires: boolean;
          derniereCommandeA: string | null;
        }>;
      }>("/api/stores/etat"),
    /**
     * Le salaire mensuel du responsable commande de ce magasin, en DIRHAMS.
     * Reserve a l'administrateur : il sert au cout d'une commande.
     */
    salaireResponsable: (storeId: string, montant: number) =>
      apiRequest<{ success: boolean; salaireCentimes: number }>(
        `/api/stores/${storeId}/salaire-responsable`,
        { method: "PUT", body: JSON.stringify({ montant }) },
      ),
    getAll: async () => {
      const result = await apiRequest<{ stores: any[] }>("/api/stores");
      return result.stores;
    },
    /**
     * Les articles que Glovo ne rend plus. Ils ne sont pas en attente de
     * suppression : plus rien ne supprime ici. Ils attendent que Glovo les
     * rende, et la synchronisation les sort alors de la quarantaine.
     */
    quarantaine: (id: string) =>
      apiRequest<{ produits: any[]; total: number }>(
        `/api/stores/${id}/quarantaine`,
      ),
    // `viderQuarantaine` a ete retiree : elle effacait des centaines
    // d'articles d'un appel. Personne ne supprime un article — un retrait
    // se demande, et la direction approuve ou refuse.
    getById: async (id: string) => {
      const result = await apiRequest<{ store: any }>(`/api/stores/${id}`);
      return result.store;
    },
    create: (data: any) =>
      apiRequest<any>("/api/stores", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      apiRequest<any>(`/api/stores/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiRequest<void>(`/api/stores/${id}`, {
        method: "DELETE",
      }),
    /** L'emballage d'un article, corrige a la main par l'admin. */
    majEmballageProduit: (
      storeId: string,
      sku: string,
      corps: { nomEmballage?: string; coutEmballage?: number; nbEmballage?: number },
    ) =>
      apiRequest<{
        success: boolean;
        sku: string;
        nomEmballage: string | null;
        coutEmballage: number | null;
        nbEmballage: number | null;
        emballageManuel: boolean;
      }>(`/api/stores/${storeId}/produits/${encodeURIComponent(sku)}/emballage`, {
        method: "PUT",
        body: JSON.stringify(corps),
      }),

    /**
     * Le nom de l'article en darija, corrige a la main. Reserve a l'admin.
     * Une chaine vide efface la correction et rend la main au fichier.
     */
    /**
     * Le prix de vente : envoye a Glovo, puis enregistre chez nous.
     * Une erreur ici veut dire que Glovo a refuse et que rien n'a bouge.
     */
    majPrixGlovo: (storeId: string, sku: string, prixGlovo: number) =>
      apiRequest<{
        success: boolean;
        sku: string;
        glovoPrice: number;
        avant: number;
      }>(
        `/api/stores/${storeId}/produits/${encodeURIComponent(sku)}/prix-glovo`,
        { method: "PUT", body: JSON.stringify({ prixGlovo }) },
      ),

    /** Changer le fournisseur d'UN article du catalogue (direction, support). */
    majFournisseurProduit: (storeId: string, sku: string, fournisseurId: string) =>
      apiRequest<{ success: boolean; sku: string; fournisseurId: string; fournisseurNom: string }>(
        `/api/stores/${storeId}/produits/${encodeURIComponent(sku)}/fournisseur`,
        { method: "PUT", body: JSON.stringify({ fournisseurId }) },
      ),
    /** Copier les noms arabes corriges de ce magasin vers les autres (04/11/2026). */
    propagerNomsArabes: (storeId: string) =>
      apiRequest<{ success: boolean; corriges: number; copies: number; gardes: number; magasins: number }>(
        `/api/stores/${storeId}/noms-ar/propager`,
        { method: "POST", body: "{}" },
      ),
    majNomArabeProduit: (storeId: string, sku: string, nomAr: string) =>
      apiRequest<{ success: boolean; sku: string; nomAr: string | null; autresMagasins?: number }>(
        `/api/stores/${storeId}/produits/${encodeURIComponent(sku)}/nom-ar`,
        { method: "PUT", body: JSON.stringify({ nomAr }) },
      ),

    /** Le prix d'achat saisi a la main (28/10/2026), trace avec le nom. */
    majPrixAchatProduit: (storeId: string, sku: string, prixAchat: number) =>
      apiRequest<{ success: boolean; sku: string; prixAchat: number; avant: number | null; parNom: string; le: string }>(
        `/api/stores/${storeId}/produits/${encodeURIComponent(sku)}/prix-achat`,
        { method: "PUT", body: JSON.stringify({ prixAchat }) },
      ),

    syncProducts: (id: string) =>
      apiRequest<{ success: boolean; message: string; stats: any }>(
        `/api/stores/${id}/sync-products`,
        {
          method: "POST",
        },
      ),
    /**
     * Telecharge le fichier « All product » du magasin.
     *
     * On n'utilise pas apiRequest : il decode du JSON, et ici la reponse est
     * un classeur Excel. Le fichier est recupere en binaire puis remis au
     * navigateur.
     */
    telechargerConcordance: async (storeId: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Non authentifié");

      const reponse = await fetch(
        `${API_BASE_URL}/api/stores/${storeId}/concordance-glovo`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!reponse.ok) {
        let message = "Concordance impossible";
        try {
          message = (await reponse.json())?.message || message;
        } catch {
          /* la reponse n'etait pas du JSON */
        }
        throw new Error(message);
      }

      const nom =
        /filename="([^"]+)"/.exec(
          reponse.headers.get("Content-Disposition") || "",
        )?.[1] || "concordance-glovo.xlsx";

      const blob = await reponse.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      URL.revokeObjectURL(url);
    },

    telechargerAllProduct: async (storeId: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Non authentifié");

      const reponse = await fetch(
        `${API_BASE_URL}/api/stores/${storeId}/all-product`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!reponse.ok) {
        let message = "Export impossible";
        try {
          message = (await reponse.json())?.message || message;
        } catch {
          /* la reponse n'etait pas du JSON */
        }
        throw new Error(message);
      }

      const nom =
        /filename="([^"]+)"/.exec(
          reponse.headers.get("Content-Disposition") || "",
        )?.[1] || "all-product.xlsx";

      const blob = await reponse.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      document.body.removeChild(lien);
      URL.revokeObjectURL(url);
      return nom;
    },

    /**
     * Le catalogue d'UNE source de revenu, en Excel.
     *
     * Trois fichiers differents, pas trois fois le meme : chaque canal a
     * ses propres articles et son propre prix. Le serveur s'en charge, on
     * ne lui envoie que le nom du canal.
     */
    telechargerCatalogueCanal: async (
      storeId: string,
      canal: "glovo" | "site" | "kooul",
    ) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Non authentifié");

      const reponse = await fetch(
        `${API_BASE_URL}/api/stores/${storeId}/catalogue/${canal}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!reponse.ok) {
        let message = `Export ${canal} impossible`;
        try {
          message = (await reponse.json())?.message || message;
        } catch {
          /* la reponse n'etait pas du JSON */
        }
        throw new Error(message);
      }

      const nom =
        /filename="([^"]+)"/.exec(
          reponse.headers.get("Content-Disposition") || "",
        )?.[1] || `catalogue-${canal}.xlsx`;

      const blob = await reponse.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      document.body.removeChild(lien);
      URL.revokeObjectURL(url);
      return nom;
    },

    getProducts: async (
      storeId: string,
      options?: {
        page?: number;
        limit?: number;
        search?: string;
        category?: string;
        /** id du fournisseur, « CAISSE » ou « AUCUN ». */
        fournisseur?: string;
        /** « ACTIF », « INACTIF » ou « QUARANTAINE ». */
        statut?: string;
      },
    ) => {
      const params = new URLSearchParams();
      if (options?.page) params.append("page", options.page.toString());
      if (options?.limit) params.append("limit", options.limit.toString());
      if (options?.search) params.append("search", options.search);
      if (options?.category) params.append("category", options.category);
      if (options?.fournisseur) params.append("fournisseur", options.fournisseur);
      if (options?.statut) params.append("statut", options.statut);
      const query = params.toString();
      return apiRequest<{
        products: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
        storeId: string;
        storeName: string;
      }>(`/api/stores/${storeId}/products${query ? `?${query}` : ""}`);
    },
    getCategories: async (storeId: string) => {
      return apiRequest<{
        categories: string[];
        total: number;
      }>(`/api/stores/${storeId}/categories`);
    },
    getMessagingSettings: async (storeId: string) => {
      return apiRequest<{
        autoMessagingEnabled: boolean;
        autoCancellationMessagingEnabled: boolean;
        messagingTestMode: boolean;
        messagingTestPhone: string;
        messagingTemplateSid: string;
      }>(`/api/stores/${storeId}/messaging-settings`);
    },
    updateMessagingSettings: async (storeId: string, settings: any) => {
      return apiRequest<{ success: boolean; message: string; settings: any }>(
        `/api/stores/${storeId}/messaging-settings`,
        {
          method: "PUT",
          body: JSON.stringify(settings),
        },
      );
    },
    sendTestMessage: async (storeId: string, testPhone: string) => {
      return apiRequest<{
        success: boolean;
        message: string;
        messageSid?: string;
      }>(`/api/stores/${storeId}/test-message`, {
        method: "POST",
        body: JSON.stringify({ testPhone }),
      });
    },
    clearProductsAndCategories: () =>
      apiRequest<{
        success: boolean;
        message: string;
        stats: {
          products: number;
          storeProducts: number;
          categories: number;
        };
      }>("/api/stores/clear-products-categories", {
        method: "POST",
      }),
  },

  // ============ ORDERS ============
  orders: {
    /**
     * ESSAI — ajouter un article a une commande Glovo deja passee.
     *
     * Reserve a l'administration. Sans `envoyer`, rien ne part chez Glovo :
     * la reponse contient seulement l'apercu du montant qui serait confirme.
     * Avec `envoyer: true`, l'appel est reel et la reponse brute de Glovo
     * est rendue telle quelle. Dans les deux cas, RIEN n'est enregistre de
     * notre cote : c'est un essai, pas un ajout.
     */
    essaiAjoutGlovo: (
      orderId: string,
      corps: {
        sku: string;
        prixUnitaire: number;
        quantite?: number;
        poids?: number;
        unite?: "MAD" | "CENTIMES";
        envoyer?: boolean;
      },
    ) =>
      apiRequest<any>(
        `/api/orders/${encodeURIComponent(orderId)}/essai-ajout-glovo`,
        { method: "POST", body: JSON.stringify(corps) },
      ),

    getStats: async (filters?: { storeId?: string; dateFilter?: string; source?: string }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      if (filters?.dateFilter) params.append("dateFilter", filters.dateFilter);
      // La plateforme (01/11/2026) : les tuiles suivent le filtre de la liste.
      if (filters?.source) params.append("source", filters.source);
      const query = params.toString();
      return apiRequest<{
        today: {
          orders: number;
          revenue: number;
          ordersAll: number;
          cancelledOrders: number;
          cancelledRevenue: number;
          revenueAll: number;
          /** Articles retirés en préparation. Alimenté par l'import Glovo. */
          lostSales?: number;
          /** Commission et frais retenus par Glovo. Idem. */
          deductions?: number;
          /** Ce que Glovo a rendu au client et nous retient. */
          refunds?: number;
        };
        period: {
          orders: number;
          revenue: number;
          ordersAll: number;
          cancelledOrders: number;
          cancelledRevenue: number;
          revenueAll: number;
          lostSales?: number;
          deductions?: number;
          refunds?: number;
        };
        byStatus: Record<string, { count: number; revenue: number }>;
      }>(`/api/orders/stats${query ? `?${query}` : ""}`);
    },
    /** Le detail derriere la tuile « Deductions ». */
    getRemboursements: async (filters?: {
      storeId?: string;
      dateFilter?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      if (filters?.dateFilter) params.append("dateFilter", filters.dateFilter);
      const query = params.toString();
      return apiRequest<{
        total: number;
        tronquee: boolean;
        commandes: {
          id: string;
          code: string;
          magasin: string;
          montantCentimes: number;
          recueA: string | null;
          justificatifs: { id: string; article: string | null; motif: string | null }[];
        }[];
      }>(`/api/orders/remboursements${query ? `?${query}` : ""}`);
    },

    /** Ce que le magasin doit acheter a la place d'un fournisseur. */
    getAchatsCaisse: async (statut = "DEMANDE") =>
      apiRequest<{
        total: number;
        lignes: {
          id: string;
          orderId: string | null;
          numero: string;
          article: string;
          quantite: number;
          prixMaxCentimes: number;
          prixPayeCentimes: number | null;
          statut: string;
          motif: string | null;
          demandeLe: string | null;
          /** Volaille achetee en caisse : on pese au lieu de taper un prix (18/10/2026). */
          auPoids?: boolean;
          /** Prix du kilo du jour deja donne pour cet article, sinon null. */
          prixKgConnu?: number | null;
        }[];
      }>(`/api/orders/achats-caisse?statut=${encodeURIComponent(statut)}`),

    /** Volaille achetee en caisse : prix du kilo du jour du magasin (18/10/2026). */
    prixCaisseDuJour: (storeId?: string) =>
      apiRequest<{
        auPoids: boolean;
        aJour?: boolean;
        manquants?: number;
        jour?: string;
        prix?: Record<string, number>;
        saisiLe?: string | null;
        saisiPar?: string;
        articles?: Array<{
          id: string;
          nom: string;
          nomAr: string | null;
          photo: string | null;
          ref: string | null;
          refNom: string | null;
          refNomAr: string | null;
          prixKg: number | null;
          dernier: number | null;
        }>;
        references?: Array<{
          id: string;
          nom: string;
          nomAr: string;
          photo: string | null;
          prixKg: number | null;
          dernier: number | null;
          articles: string[];
        }>;
      }>(`/api/orders/caisse-poids/prix-du-jour${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`),
    enregistrerPrixCaisseDuJour: (prix: Record<string, number>, storeId?: string) =>
      apiRequest<{ ok: boolean; jour: string; prix: Record<string, number> }>(
        "/api/orders/caisse-poids/prix-du-jour",
        { method: "POST", body: JSON.stringify({ prix, storeId }) },
      ),

    /** Solder un achat : soit il est fait, soit l'article est introuvable. */
    /**
     * Le responsable paie l'article par la caisse (21/10/2026) : il donne le
     * TOTAL de la ligne ; le serveur divise par la quantite.
     */
    payerCaisse: (orderId: string, itemId: string, totalPayeDH: number) =>
      apiRequest<{ ok: boolean; depenseId: string; unitCentimes: number; totalCentimes: number }>(
        `/api/orders/${orderId}/articles/${itemId}/payer-caisse`,
        { method: "POST", body: JSON.stringify({ totalPayeDH }) },
      ),
    soldierAchatCaisse: async (
      id: string,
      corps: {
        prixPayeCentimes?: number;
        introuvable?: boolean;
        erreurFournisseur?: boolean;
        motif?: string;
        /** Volaille au poids : le poids pese, et le prix du kilo s'il manque. */
        poidsKg?: number;
        prixKg?: number;
      },
    ) =>
      apiRequest<{ success: boolean; statut: string; depassement?: boolean }>(
        `/api/orders/achats-caisse/${id}`,
        { method: "POST", body: JSON.stringify(corps) },
      ),

    /** Direction / support : corriger un achat caisse deja saisi (trace gardee). */
    corrigerAchatCaisse: async (
      id: string,
      corps: { poidsKg?: number; prixKg?: number; prixPayeDH?: number },
    ) =>
      apiRequest<{
        success: boolean;
        inchange?: boolean;
        payeCentimes?: number;
        depassement?: boolean;
        prixVenteLigneDH?: number | null;
        corrigePar?: string;
      }>(`/api/orders/achats-caisse/${id}/corriger`, {
        method: "POST",
        body: JSON.stringify(corps),
      }),

    /** Les annulees de la periode : en dirhams et par responsable commande (02/11/2026). */
    annulations: (filters?: { storeId?: string; dateFilter?: string; source?: string }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      if (filters?.dateFilter) params.append("dateFilter", filters.dateFilter);
      if (filters?.source) params.append("source", filters.source);
      return apiRequest<{
        nombre: number;
        montantCentimes: number | null;
        payeesCentimes: number | null;
        parResponsable: Array<{
          nom: string;
          nombre: number;
          montantCentimes: number | null;
          parGlovo: number;
          parNous: number;
          payeesCentimes: number | null;
          par: Record<string, number>;
        }>;
        sauvegarde?: { cle: string; calculeLe: string | null };
      }>(`/api/orders/annulations?${params.toString()}`);
    },
    /** Les quinzaines de la page Commandes et leur cloture (02/11/2026). */
    clotures: () =>
      apiRequest<{
        quinzaines: Array<{ cle: string; filtre: string; cloturee: boolean; terminee: boolean }>;
      }>(`/api/orders/clotures`),
    /** Livreur envoye a nos frais pour un article oublie (02/11/2026). */
    livreurSupplementaire: (orderId: string, d: { montant: number; caisseId: string; motif?: string; articleId?: string }) =>
      apiRequest<{ ok: boolean; depenseId: string }>(`/api/orders/${orderId}/livreur-supplementaire`, {
        method: "POST",
        body: JSON.stringify(d),
      }),
    annulerLivreurSupplementaire: (orderId: string, depenseId: string) =>
      apiRequest<{ ok: boolean }>(
        `/api/orders/${orderId}/livreur-supplementaire/${encodeURIComponent(depenseId)}/annuler`,
        { method: "POST" },
      ),
    /** Corriger le prix de vente d'un article, a l'unite, en DH (02/11/2026). */
    prixVente: (orderId: string, itemId: string, prix: number) =>
      apiRequest<{ ok: boolean; inchange?: boolean; avantCentimes?: number; apresCentimes?: number }>(
        `/api/orders/${orderId}/articles/${itemId}/prix-vente`,
        { method: "PUT", body: JSON.stringify({ prix }) },
      ),
    cloturer: (cle: string, cloturee: boolean) =>
      apiRequest<{ ok: boolean; cle: string; cloturee: boolean; filtre: string }>(
        `/api/orders/clotures/${encodeURIComponent(cle)}`,
        { method: "PUT", body: JSON.stringify({ cloturee }) },
      ),
    getVentesPerdues: async (filters?: {
      storeId?: string;
      dateFilter?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      if (filters?.dateFilter) params.append("dateFilter", filters.dateFilter);
      const query = params.toString();
      return apiRequest<{
        total: number;
        tronquee: boolean;
        commandes: {
          id: string;
          code: string;
          magasin: string;
          statut: string;
          recueA: string | null;
          creeeLe: string | null;
          perduCentimes: number;
          montantFactureCentimes: number;
          articles: { nom: string; quantite: number; prixCentimes: number }[];
          /** Le responsable commande de la commande (01/11/2026). */
          responsable?: string | null;
          /** Les retraits demandes dans l'application, et par qui. */
          retraitsDemandes?: { article: string; par: string }[];
        }[];
      }>(`/api/orders/ventes-perdues${query ? `?${query}` : ""}`);
    },
    getAll: async (filters?: {
      storeId?: string;
      status?: string;
      limit?: number;
      page?: number;
      dateFilter?: string;
      phone?: string;
      code?: string;
      source?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      // LA PLATEFORME (10/10/2026). Elle n'etait filtree qu'a l'ecran, sur
      // les 30 commandes de la page : « Site » sur une page de commandes
      // Glovo ne montrait rien, alors que les commandes du site existaient.
      if (filters?.source) params.append("source", filters.source);
      if (filters?.status) params.append("status", filters.status);
      if (filters?.dateFilter) params.append("dateFilter", filters.dateFilter);
      if (filters?.phone) params.append("phone", filters.phone); // 🔥 Filtre phone côté serveur
      // LE NUMERO CHERCHE. Il etait bien saisi a l'ecran et bien traite par
      // le serveur, mais jamais envoye : la liste restait celle de la
      // periode, sans la commande cherchee.
      if (filters?.code) params.append("code", filters.code);
      params.append("limit", String(filters?.limit || 30));
      params.append("page", String(filters?.page || 1));
      const query = params.toString();
      const result = await apiRequest<{ orders: any[]; pagination: any }>(
        `/api/orders${query ? `?${query}` : ""}`,
      );
      return result; // Retourner { orders, pagination }
    },
    /**
     * Le compteur de changements des commandes (1 document). Les listes ne
     * se rechargent que quand il bouge — voir useVersionCommandes.
     */
    version: () => apiRequest<{ n: number }>("/api/orders/version"),
    /** Commandes arrivees depuis `depuis` (alarme du responsable commande). */
    arrivees: (depuis: string, storeId?: string | null) =>
      apiRequest<{ commandes: { id: string; code: string }[] }>(
        `/api/orders/arrivees?depuis=${encodeURIComponent(depuis)}` +
          (storeId ? `&storeId=${encodeURIComponent(storeId)}` : ""),
      ),
    getById: async (id: string) => {
      const result = await apiRequest<any>(`/api/orders/${id}`);
      return result;
    },
    // --- DEUX RESPONSABLES DANS LE MEME MAGASIN (15/10/2026) -------------
    /** L'equipe du magasin et qui est en service aujourd'hui. */
    equipe: (storeId?: string | null) =>
      apiRequest<{
        moi: string | null;
        storeId: string | null;
        delaiMinutes: number;
        equipe: Array<{
          uid: string;
          nom: string;
          storeId: string;
          enService: boolean;
          depuis: string | null;
          enPause?: boolean;
          pauseJusqua?: string | null;
          reposAujourdhui?: boolean;
          jourRepos?: number | null;
        }>;
      }>(`/api/orders/equipe${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`),
    /** Debut ou fin de service du responsable connecte. */
    service: (enService: boolean) =>
      apiRequest<{ enService: boolean; recues: number; transferees: number; gardees: number }>(
        "/api/orders/service",
        { method: "POST", body: JSON.stringify({ enService }) },
      ),
    /** Pause dejeuner (30 min, fin automatique) ou retour de pause. */
    pause: (pause: boolean) =>
      apiRequest<{ enPause: boolean; pauseJusqua: string | null; transferees: number; gardees: number }>(
        "/api/orders/pause",
        { method: "POST", body: JSON.stringify({ pause }) },
      ),
    /** « Je m'en occupe », « Je la prends », ou « Reprendre » (forcer). */
    prendre: (id: string, forcer = false) =>
      apiRequest<{ statut: "prise-en-charge" | "prise" | "reprise"; avant?: string | null }>(
        `/api/orders/${id}/prendre`,
        { method: "POST", body: JSON.stringify({ forcer }) },
      ),
    /** Le suivi par responsable (tableau de bord). */
    suiviResponsables: (jours: 1 | 7 | 30, storeId?: string | null) =>
      apiRequest<{
        jours: number;
        depuis: string;
        delaiMinutes: number;
        nonAttribuees: number;
        lignes: Array<{
          uid: string;
          nom: string;
          storeId: string;
          commandes: number;
          terminees: number;
          annulees: number;
          enCours: number;
          priseEnChargeMoyMin: number | null;
          preparationMoyMin: number | null;
          retards: number;
          reprisesSubies: number;
          remboursees: number;
          remboursementCentimes: number;
          ventePerdueCentimes: number;
        }>;
      }>(
        `/api/orders/responsables/suivi?jours=${jours}` +
          (storeId && storeId !== "ALL" ? `&storeId=${encodeURIComponent(storeId)}` : ""),
      ),
    accept: (id: string) =>
      apiRequest<any>(`/api/orders/${id}/accept`, {
        method: "POST",
      }),
    markReady: (id: string) =>
      apiRequest<any>(`/api/orders/${id}/ready`, {
        method: "POST",
      }),
    /** Commande du site : attribuer / changer le magasin qui la traite. */
    /** Frais de livraison (DH) et moyen de paiement d'une commande du site. */
    livraison: (
      id: string,
      donnees: {
        fraisLivraison: number;
        moyenPaiement: "ESPECES" | "VIREMENT" | "CARTE";
        /** La caisse qui paie le livreur (25/10/2026). */
        caisseLivreurId?: string;
        /** Livraison offerte : le client ne paie que les articles (02/11/2026). */
        livraisonOfferte?: boolean;
      },
    ) =>
      apiRequest<{
        ok: boolean;
        deliveryFee: number;
        livraisonOfferte?: boolean;
        totalAmount: number;
        moyenPaiement: string;
        livreurPaye?: { responsableId: string; depenseId: string; montantCentimes: number } | null;
      }>(
        `/api/orders/${id}/livraison`,
        { method: "PUT", body: JSON.stringify(donnees) },
      ),
    /** Paiement d'une commande du site : encaisse ou en attente. */
    paiement: (
      id: string,
      donnees: {
        encaisse: boolean;
        moyenPaiement?: "ESPECES" | "VIREMENT" | "CARTE";
        /** La caisse qui encaisse et paie le livreur (25/10/2026). */
        caisseId?: string;
      },
    ) =>
      apiRequest<{
        ok: boolean;
        paiementEncaisse: boolean;
        moyenPaiement: string | null;
        caisseEncaissement?: { responsableId: string; alimentationId: string | null; depenseLivreurId: string | null } | null;
      }>(
        `/api/orders/${id}/paiement`,
        { method: "PUT", body: JSON.stringify(donnees) },
      ),
    /** Ajouter un article du catalogue a une commande du site (direction, support). */
    ajouterArticle: (
      id: string,
      donnees: { sku: string; quantite: number; prixUnitaire?: number; dejaSurTablette?: boolean },
    ) =>
      apiRequest<{
        ok: boolean;
        surGlovo?: boolean;
        itemId: string;
        nom: string;
        prixUnitaire: number;
        prixLigne: number;
        basketId: string | null;
        panierCree: boolean;
        enAttenteDeValidation: boolean;
      }>(`/api/orders/${id}/articles`, { method: "POST", body: JSON.stringify(donnees) }),
    /** Commande du site deja livree hors application : la passer livree, sans message. */
    regulariserLivree: (id: string, sacs: { sacKraft: number; sacTissu: number }) =>
      apiRequest<{ ok: boolean; statutAvant: string; paniersCrees: boolean; paniersFermes: number }>(
        `/api/orders/${id}/regulariser-livree`,
        { method: "POST", body: JSON.stringify(sacs) },
      ),
    /** Commande du site passee « livree » par erreur : retour en preparation, fournisseurs prevenus. */
    remettreEnPreparation: (id: string) =>
      apiRequest<{ ok: boolean; paniers: number; prevenus: number }>(
        `/api/orders/${id}/remettre-en-preparation`,
        { method: "POST" },
      ),
    /** Deplacer UN article de la commande vers un autre fournisseur. */
    /** `prixAchat` (DH, a l'unite) : seulement pour un passage en caisse. */
    /** `quantite` (29/10/2026) : une partie seulement de la ligne en caisse. */
    deplacerArticle: (id: string, itemId: string, fournisseurId: string, prixAchat?: number, quantite?: number) =>
      apiRequest<{
        ok: boolean;
        basketId: string;
        panierCree: boolean;
        ancienAnnule: boolean;
        partiel?: { quantite: number; sur: number };
      }>(
        `/api/orders/${id}/articles/${itemId}/fournisseur`,
        { method: "PUT", body: JSON.stringify({ fournisseurId, prixAchat, quantite }) },
      ),
    attribuerMagasin: (id: string, storeId: string) =>
      apiRequest<any>(`/api/site/admin/attribuer/${id}`, {
        method: "PUT",
        body: JSON.stringify({ storeId }),
      }),
    /**
     * Marquer livree, avec les emballages partis chez le client.
     *
     * Les deux nombres ne sont pas optionnels : le serveur refuse une
     * livraison a zero sac. Une commande part forcement dans quelque chose,
     * et un comptage a trous ne sert a rien.
     */
    markDelivered: (id: string, sacs: { sacKraft: number; sacTissu: number }) =>
      apiRequest<any>(`/api/orders/${id}/delivered`, {
        method: "POST",
        body: JSON.stringify(sacs),
      }),
    cancel: (id: string, reason?: string) =>
      apiRequest<any>(`/api/orders/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
      }),
    updatePhone: (id: string, phone: string) =>
      apiRequest<any>(`/api/orders/${id}/phone`, {
        method: "PUT",
        body: JSON.stringify({ phone }),
      }),
    debug: (id: string) => apiRequest<any>(`/api/orders/${id}/debug`),
  },

  // ============ PRODUCTS ============
  products: {
    /* La description lue par le client sur le site, dans les deux langues.
       Elle est portee par le produit GLOBAL : ecrite une fois, elle sort a
       Oulfa comme a Bourgogne. D'ou l'appel sur /api/products et non sur le
       magasin. Un champ vide efface la description. */
    majDescriptionProduit: (
      sku: string,
      descriptionFr: string,
      descriptionAr: string,
    ) =>
      apiRequest<{
        success: boolean;
        descriptionFr: string | null;
        descriptionAr: string | null;
      }>(`/api/products/${encodeURIComponent(sku)}/description`, {
        method: "PUT",
        body: JSON.stringify({ descriptionFr, descriptionAr }),
      }),

    getAll: async (filters?: {
      storeId?: string;
      fournisseurId?: string;
      page?: number;
      limit?: number;
      search?: string;
      categoryId?: string;
      status?: string;
    }) => {
      const params = new URLSearchParams();
      if (filters?.storeId) params.append("storeId", filters.storeId);
      // Les filtres partent au serveur : lui seul voit le catalogue entier.
      if (filters?.categoryId) params.append("categoryId", filters.categoryId);
      if (filters?.status && filters.status !== "ALL")
        params.append("status", filters.status);
      if (filters?.fournisseurId)
        params.append("fournisseurId", filters.fournisseurId);
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.limit) params.append("limit", filters.limit.toString());
      if (filters?.search) params.append("search", filters.search);
      const query = params.toString();
      return apiRequest<{
        products: any[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
        totalActifs?: number;
        totalInactifs?: number;
        totalStatut?: number;
        facettes?: Array<{ id: string; nom: string; nombre: number }>;
      }>(`/api/products${query ? `?${query}` : ""}`);
    },
    getById: async (id: string) => {
      const result = await apiRequest<{ product: any }>(`/api/products/${id}`);
      return result.product;
    },
    create: (data: any) =>
      apiRequest<any>("/api/products", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      apiRequest<any>(`/api/products/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiRequest<void>(`/api/products/${id}`, {
        method: "DELETE",
      }),
    updatePrice: (id: string, priceData: any) =>
      apiRequest<any>(`/api/products/${id}/price`, {
        method: "PUT",
        body: JSON.stringify(priceData),
      }),
    syncGlovo: (id: string) =>
      apiRequest<any>(`/api/products/${id}/sync-glovo`, {
        method: "POST",
      }),

    // ---- Ruptures declarees par le fournisseur -------------------------
    /** Le produit sort de la vente, chez nous et chez Glovo, jusqu'a l'heure dite. */
    rupture: (
      id: string,
      data: { duree: "30min" | "heure" | "finJournee"; heure?: string },
    ) =>
      apiRequest<{
        success: boolean;
        indisponibleJusqua: string;
        glovoOk: boolean;
        glovoErreur: string | null;
      }>(`/api/products/${id}/rupture`, {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /** Remise en vente immediate, sans attendre l'heure prevue. */
    retour: (id: string) =>
      apiRequest<{ success: boolean; glovoOk: boolean; glovoErreur: string | null }>(
        `/api/products/${id}/retour`,
        { method: "POST" },
      ),

    /**
     * La meme remise en vente, declenchee par la direction.
     *
     * Le fournisseur n'a pas a dire de quel magasin il parle : le serveur le
     * deduit de son profil. La direction voit les deux magasins — d'ou le
     * storeId, sans lequel le serveur refuse l'appel.
     *
     * Il n'existe volontairement PAS de fonction inverse ici : retirer un
     * article de la vente reste une demande du fournisseur que le support
     * valide.
     */
    remettreEnVente: (id: string, storeId: string) =>
      apiRequest<{ success: boolean; glovoOk: boolean; glovoErreur: string | null }>(
        `/api/products/${id}/retour?storeId=${encodeURIComponent(storeId)}`,
        { method: "POST" },
      ),

    /** Demande de retrait definitif — decidee par le responsable. */
    demanderSuppression: (id: string, motif?: string) =>
      apiRequest<{ success: boolean; dejaDemande: boolean; message?: string }>(
        `/api/products/${id}/suppression`,
        { method: "POST", body: JSON.stringify({ motif }) },
      ),

    /** Cote administration : les demandes en attente. */
    demandesSuppression: () =>
      apiRequest<{ demandes: any[] }>("/api/products/demandes-suppression"),

    /** Cote administration : valider ou refuser une demande. */
    deciderSuppression: (demandeId: string, decision: "valider" | "refuser") =>
      apiRequest<{ success: boolean }>(
        `/api/products/demandes-suppression/${demandeId}`,
        { method: "POST", body: JSON.stringify({ decision }) },
      ),
  },

  // ============ SERVICE DU FOURNISSEUR ============
  service: {
    /** Son etat, ses horaires, ses conges. */
    lire: () =>
      apiRequest<{
        enService: boolean;
        enConge: boolean;
        enServiceDepuis: string | null;
        horaires: { debut: number; fin: number };
        conges: Array<{ id: string; debut: string; fin: string; motif: string | null }>;
      }>("/api/fournisseurs/mon-service"),

    /** Ouvrir ou fermer. Le catalogue Glovo suit dans la foulee. */
    basculer: (ouvrir: boolean) =>
      apiRequest<{
        success: boolean;
        enService: boolean;
        envoyes: number;
        glovoOk: boolean;
        glovoErreur: string | null;
      }>("/api/fournisseurs/mon-service", {
        method: "POST",
        body: JSON.stringify({ ouvrir }),
      }),

    enregistrerHoraires: (debut: number, fin: number) =>
      apiRequest<{ success: boolean }>("/api/fournisseurs/mes-horaires", {
        method: "PUT",
        body: JSON.stringify({ debut, fin }),
      }),

    /**
     * Ses horaires jour par jour. Les SEPT jours sont exiges par le serveur,
     * y compris les jours fermes.
     */
    enregistrerSemaine: (
      jours: Array<{ jour: number; ferme: boolean; debut: number; fin: number }>,
    ) =>
      apiRequest<{ success: boolean }>(
        "/api/fournisseurs/mes-horaires-semaine",
        { method: "PUT", body: JSON.stringify({ jours }) },
      ),

    poserConge: (debut: string, fin: string, motif?: string) =>
      apiRequest<{ success: boolean; fermeMaintenant: boolean }>(
        "/api/fournisseurs/mes-conges",
        { method: "POST", body: JSON.stringify({ debut, fin, motif }) },
      ),

    retirerConge: (congeId: string) =>
      apiRequest<{ success: boolean }>(
        `/api/fournisseurs/mes-conges/${congeId}`,
        { method: "DELETE" },
      ),

    /** Cote administration : forcer l'etat d'un fournisseur. */
    forcer: (fournisseurId: string, ouvrir: boolean) =>
      apiRequest<{ success: boolean; enService: boolean }>(
        `/api/fournisseurs/${fournisseurId}/service`,
        { method: "POST", body: JSON.stringify({ ouvrir }) },
      ),
  },

  // ============ BASKETS ============
  /**
   * RETIRER UN ARTICLE INDISPONIBLE D'UNE COMMANDE (21/10/2026).
   *
   * Le responsable DEMANDE ; le patron valide ou refuse. A la validation,
   * l'article est retire chez Glovo par l'API, ou a defaut sur la tablette.
   */
  retraitsCommande: {
    /** Retrait DIRECT (02/11/2026) : sans validation, part en ventes perdues. */
    retirer: (orderItemId: string, motif?: string) =>
      apiRequest<{ success: boolean; id: string; statut: string; raison: string | null; montantCentimes: number }>(
        "/api/retraits-commande/direct",
        { method: "POST", body: JSON.stringify({ orderItemId, motif: motif || null }) },
      ),
    demander: (orderItemId: string, motif?: string) =>
      apiRequest<{ success: boolean; id: string; statut: string }>(
        "/api/retraits-commande",
        { method: "POST", body: JSON.stringify({ orderItemId, motif: motif || null }) },
      ),
    liste: (statut: string = "EN_ATTENTE") =>
      apiRequest<{ retraits: RetraitCommande[]; peutDecider: boolean }>(
        `/api/retraits-commande?statut=${encodeURIComponent(statut)}`,
      ),
    valider: (id: string) =>
      apiRequest<{ success: boolean; statut: string; raison: string | null; glovo: any }>(
        `/api/retraits-commande/${id}/valider`,
        { method: "PUT" },
      ),
    refuser: (id: string) =>
      apiRequest<{ success: boolean; statut: string }>(
        `/api/retraits-commande/${id}/refuser`,
        { method: "PUT" },
      ),
  },

  baskets: {
    getAll: async (filters?: {
      orderId?: string;
      fournisseurId?: string;
      status?: string;
      limit?: number;
      page?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters?.orderId) params.append("orderId", filters.orderId);
      if (filters?.fournisseurId)
        params.append("fournisseurId", filters.fournisseurId);
      if (filters?.status) params.append("status", filters.status);
      params.append("limit", String(filters?.limit || 30));
      params.append("page", String(filters?.page || 1));
      const query = params.toString();
      const result = await apiRequest<{ baskets: any[]; pagination: any }>(
        `/api/baskets${query ? `?${query}` : ""}`,
      );
      return result;
    },
    getById: async (id: string) => {
      const result = await apiRequest<{ basket: any }>(`/api/baskets/${id}`);
      return result.basket;
    },
    /**
     * « Je m'y mets. » Fait passer le panier en preparation.
     *
     * C'est aussi ce qui fait taire l'alarme : elle sonne tant qu'il reste
     * un panier en attente, et s'arrete quand il n'y en a plus.
     */
    commencer: (id: string) =>
      apiRequest<{ success: boolean; status: string }>(
        `/api/baskets/${id}/commencer`,
        { method: "POST" },
      ),

    markReady: (id: string) =>
      apiRequest<any>(`/api/baskets/${id}/ready`, {
        method: "POST",
      }),
    /**
     * Le fournisseur declare qu'il n'a pas l'article. Il sort de ce qu'on
     * lui doit, et un achat caisse est ouvert pour Hakim.
     */
    /** Le poids reel d'un article (fournisseur vendu au poids). */
    peser: (basketId: string, orderItemId: string, poidsKg: number, prixKg?: number) =>
      apiRequest<{ ok: boolean; poidsReelKg: number; prixKgApplique: number; montantPeseDH: number }>(
        `/api/baskets/${basketId}/poids`,
        { method: "POST", body: JSON.stringify({ orderItemId, poidsKg, ...(prixKg ? { prixKg } : {}) }) },
      ),

    declarerIndisponible: (
      basketId: string,
      orderItemId: string,
      motif?: string,
    ) =>
      apiRequest<{
        success: boolean;
        depenseId?: string;
        prixMaxCentimes?: number;
        dejaDeclare?: boolean;
      }>(`/api/baskets/${basketId}/indisponible`, {
        method: "POST",
        body: JSON.stringify({ orderItemId, motif }),
      }),

    pickup: (id: string) =>
      apiRequest<any>(`/api/baskets/${id}/pickup`, {
        method: "POST",
      }),
  },

  // ============ AUTH ============
  auth: {
    /** Le signe de vie d'un ecran ouvert. */
    signalDeVie: () =>
      apiRequest<{ success: boolean }>("/api/auth/presence", { method: "POST" }),

    changePassword: (userId: string, newPassword: string) =>
      apiRequest<{ success: boolean; message: string }>(
        "/api/auth/change-password",
        {
          method: "PUT",
          body: JSON.stringify({ userId, newPassword }),
        },
      ),
  },

  // ============ CATEGORIES ============
  categories: {
    getAll: async (filters?: { isActive?: boolean }) => {
      const params = new URLSearchParams();
      if (filters?.isActive !== undefined) {
        params.append("isActive", filters.isActive.toString());
      }
      const query = params.toString();
      const result = await apiRequest<{ categories: any[] }>(
        `/api/categories${query ? `?${query}` : ""}`,
      );
      return result.categories;
    },
    get: async (id: string) => {
      const result = await apiRequest<{ id: string; [key: string]: any }>(
        `/api/categories/${id}`,
      );
      return result;
    },
    create: (data: any) =>
      apiRequest<any>("/api/categories", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      apiRequest<any>(`/api/categories/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiRequest<{ message: string }>(`/api/categories/${id}`, {
        method: "DELETE",
      }),
  },

  // ============ ACCOUNTING ============
  accounting: {
    getSummary: () => apiRequest<any>("/api/accounting/summary"),
    getInvoices: (filters?: { fournisseurId?: string; status?: string }) => {
      const params = new URLSearchParams();
      if (filters?.fournisseurId)
        params.append("fournisseurId", filters.fournisseurId);
      if (filters?.status) params.append("status", filters.status);
      const query = params.toString();
      return apiRequest<any[]>(
        `/api/accounting/invoices${query ? `?${query}` : ""}`,
      );
    },
    getInvoice: (id: string) =>
      apiRequest<any>(`/api/accounting/invoices/${id}`),
    payInvoice: (id: string) =>
      apiRequest<any>(`/api/accounting/invoices/${id}/pay`, {
        method: "POST",
      }),
    getMySales: () => apiRequest<any>("/api/accounting/my-sales"),
  },

  // ============ DASHBOARD (mensuel) ============
  dashboard: {
    getMonthly: async (params?: {
      year?: number;
      month?: number;
      storeId?: string;
    }) => {
      const sp = new URLSearchParams();
      if (params?.year != null) sp.append("year", String(params.year));
      if (params?.month != null) sp.append("month", String(params.month));
      if (params?.storeId && params.storeId !== "ALL")
        sp.append("storeId", params.storeId);
      const q = sp.toString();
      return apiRequest<import("@/types").MonthlyDashboardResponse>(
        `/api/dashboard/monthly${q ? `?${q}` : ""}`,
      );
    },
  },

  // ============ RAPPORTS QUOTIDIENS ============
  rapports: {
    /** Les magasins que la personne connectee peut suivre. */
    magasins: () =>
      apiRequest<{ magasins: { id: string; name: string }[] }>("/api/rapports/magasins"),
    /** Le rapport d'un magasin pour un jour, avec la colonne a coller. */
    quotidien: async (date: string, storeId: string, responsableId?: string) => {
      const p = new URLSearchParams({ date, storeId });
      if (responsableId) p.append("responsableId", responsableId);
      return apiRequest<any>(`/api/rapports/quotidien?${p.toString()}`);
    },
    /** Les deux valeurs que Glovo ne met pas dans le fichier. */
    saisie: async (params: {
      date: string;
      storeId: string;
      indisponibiliteMinutes: number;
      satisfactionPourcent: number;
    }) => {
      return apiRequest<{ success: boolean }>("/api/rapports/saisie", {
        method: "POST",
        body: JSON.stringify(params),
      });
    },
  },

  // ============ MESSAGING ============
  messaging: {
    /**
     * Un vrai appel vocal, pour l'enquête Twilio sur le Maroc. Renvoie le
     * Call SID — c'est lui que leur opérateur partenaire demande.
     */
    appelTest: (data: { vers: string; depuis: string }) =>
      apiRequest<{
        success: boolean;
        callSid: string;
        statut: string;
        depuis: string;
        vers: string;
        le: string;
      }>("/api/messaging/appel-test", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    getConversations: async (
      storeId: string,
      page: number = 1,
      limit: number = 20,
    ) => {
      const params = new URLSearchParams({
        storeId,
        page: page.toString(),
        limit: limit.toString(),
      });
      return apiRequest<{
        success: boolean;
        data: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/api/messaging/conversations?${params.toString()}`);
    },
    /**
     * Les derniers messages d'un fil.
     *
     * `limite` compte des MESSAGES, pas des jours. Le parametre envoye a
     * l'API s'appelle encore `days` — un reste d'une ancienne version — et
     * le renommer des deux cotes obligerait a redeployer les fonctions pour
     * un simple changement d'ecran.
     */
    getMessages: async (
      storeId: string,
      contactNumber: string,
      limite: number = 100,
    ) => {
      const params = new URLSearchParams({
        storeId,
        contactNumber,
        days: limite.toString(),
      });
      return apiRequest<any[]>(`/api/messaging/messages?${params.toString()}`);
    },
    sendMessage: async (storeId: string, to: string, message: string) => {
      return apiRequest<{ success: boolean; message: string; data: any }>(
        "/api/messaging/send",
        {
          method: "POST",
          body: JSON.stringify({ storeId, to, message }),
        },
      );
    },
    sendTemplateMessage: async (
      storeId: string,
      to: string,
      templateSid: string,
      templateParams?: Record<string, string>,
    ) => {
      return apiRequest<{ success: boolean; message: string; data: any }>(
        "/api/messaging/send-template",
        {
          method: "POST",
          body: JSON.stringify({ storeId, to, templateSid, templateParams }),
        },
      );
    },
    reclasser: async () => {
      return apiRequest<{ success: boolean; reclassees: number }>(
        "/api/messaging/reclasser",
        { method: "POST", body: "{}" },
      );
    },
    syncMessages: async (storeId: string, limit: number = 50) => {
      return apiRequest<{
        success: boolean;
        message: string;
        stats: { synced: number; skipped: number; errors: number };
      }>("/api/messaging/sync-messages", {
        method: "POST",
        body: JSON.stringify({ storeId, limit }),
      });
    },
    refreshConversations: async (storeId?: string) => {
      return apiRequest<{
        success: boolean;
        message: string;
        updated: number;
        errors: number;
      }>("/api/messaging/refresh-conversations", {
        method: "POST",
        body: JSON.stringify({ storeId }),
      });
    },
    markAsRead: async (storeId: string, conversationId: string) => {
      return apiRequest<{ success: boolean }>(
        `/api/messaging/conversations/${conversationId}/read`,
        {
          method: "POST",
          body: JSON.stringify({ storeId }),
        },
      );
    },
    checkConfig: async (storeId: string) => {
      return apiRequest<{
        success: boolean;
        isConfigured: boolean;
        config: {
          storeId: string;
          storeName: string;
          twilioNumber: string;
        } | null;
        hasSecrets: boolean;
      }>(`/api/messaging/check-config?storeId=${storeId}`);
    },
    // Tags
    getTags: async () => {
      return apiRequest<{ id: string; label: string; color: string }[]>(
        "/api/messaging/tags",
      );
    },
    updateConversationTags: async (conversationId: string, tags: string[]) => {
      return apiRequest<{ success: boolean; tags: string[] }>(
        `/api/messaging/conversations/${conversationId}/tags`,
        {
          method: "PUT",
          body: JSON.stringify({ tags }),
        },
      );
    },
  },
  glovo: {
    getOrderDetails: async (orderId: string) => {
      return apiRequest<{
        success: boolean;
        order: any;
      }>(`/api/glovo/orders/${orderId}`);
    },
    getVendorOrders: async (storeId: string, limit?: number) => {
      const params = new URLSearchParams();
      if (limit) params.append("limit", limit.toString());
      const query = params.toString();
      return apiRequest<{
        success: boolean;
        vendorOrders: any;
      }>(`/api/glovo/vendors/${storeId}/orders${query ? `?${query}` : ""}`);
    },
    getStores: async () => {
      return apiRequest<{
        success: boolean;
        stores: Array<{
          id: string;
          name: string;
          glovoStoreId: string | null;
          isActive: boolean;
        }>;
      }>("/api/glovo/stores");
    },
  },

  // ============ ADMIN ============
  admin: {
    clearOrders: () =>
      apiRequest<{
        success: boolean;
        deleted: {
          orderItems: number;
          supplierBaskets: number;
          orders: number;
        };
      }>("/api/admin/clear-orders", { method: "POST" }),
  },

  // ============ USERS ============
  users: {
    /**
     * Les comptes. Avec `storeId`, seuls ceux de ce magasin — plus ceux qui
     * ne sont rattaches a aucun magasin (admins, support), qui travaillent
     * sur tous.
     *
     * Chaque compte revient avec `storeIdEffectif` : son magasin, pris sur
     * le compte ou, a defaut, sur sa fiche fournisseur.
     */
    getAll: async (storeId?: string) => {
      const q = storeId && storeId !== "ALL"
        ? `?storeId=${encodeURIComponent(storeId)}`
        : "";
      return apiRequest<any[]>(`/api/users${q}`);
    },
    getById: async (id: string) => {
      return apiRequest<any>(`/api/users/${id}`);
    },
    create: async (data: any) => {
      return apiRequest<{ success: boolean; userId: string }>("/api/users", {
        method: "POST",
        body: JSON.stringify(data),
      });
    },
    update: async (id: string, data: any) => {
      return apiRequest<{ success: boolean }>(`/api/users/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    delete: async (id: string) => {
      return apiRequest<{ success: boolean }>(`/api/users/${id}`, {
        method: "DELETE",
      });
    },
  },

  // ============ CUSTOMERS ============
  customers: {
    getAll: async (filters?: {
      page?: number;
      limit?: number;
      search?: string;
      storeId?: string;
      minOrders?: number;
      maxOrders?: number;
      minSpent?: number;
      maxSpent?: number;
      isVIP?: boolean;
      isActive?: boolean;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }) => {
      const params = new URLSearchParams();
      if (filters?.page) params.append("page", filters.page.toString());
      if (filters?.limit) params.append("limit", filters.limit.toString());
      if (filters?.search) params.append("search", filters.search);
      if (filters?.storeId) params.append("storeId", filters.storeId);
      if (filters?.minOrders !== undefined) params.append("minOrders", filters.minOrders.toString());
      if (filters?.maxOrders !== undefined) params.append("maxOrders", filters.maxOrders.toString());
      if (filters?.minSpent !== undefined) params.append("minSpent", filters.minSpent.toString());
      if (filters?.maxSpent !== undefined) params.append("maxSpent", filters.maxSpent.toString());
      if (filters?.isVIP !== undefined) params.append("isVIP", filters.isVIP.toString());
      if (filters?.isActive !== undefined) params.append("isActive", filters.isActive.toString());
      if (filters?.sortBy) params.append("sortBy", filters.sortBy);
      if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
      
      const query = params.toString();
      return apiRequest<{
        success: boolean;
        data: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
        stats: {
          total: number;
          active: number;
          vip: number;
          newThisMonth: number;
          newThisWeek: number;
          totalRevenue: number;
          averageOrderValue: number;
        };
      }>(`/api/customers${query ? `?${query}` : ""}`);
    },
    getById: async (customerId: string) => {
      return apiRequest<{
        success: boolean;
        data: {
          customer: any;
          orders: any[];
          conversation: any;
          stats: any;
        };
      }>(`/api/customers/${customerId}`);
    },
    exportCSV: async () => {
      const token = await getAuthToken();
      if (!token) throw new Error("User not authenticated");
      
      const url = `${API_BASE_URL}/api/customers/export/csv`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error("Export failed");
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `clients-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    },
  },

  // ============ SETTINGS ============
  settings: {
    getEmailProcessingSettings: async () => {
      return apiRequest<{
        enabled: boolean;
        startHour: number;
        endHour: number;
        timezone: string;
      }>("/api/settings/email-processing");
    },
    updateEmailProcessingSettings: async (data: {
      enabled: boolean;
      startHour: number;
      endHour: number;
      timezone: string;
    }) => {
      return apiRequest<{ success: boolean }>("/api/settings/email-processing", {
        method: "PUT",
        body: JSON.stringify(data),
      });
    },
    getDefaultPrepTime: async () => {
      return apiRequest<{ defaultPrepTime: number }>("/api/settings/default-prep-time");
    },
    updateDefaultPrepTime: async (defaultPrepTime: number) => {
      return apiRequest<{ success: boolean }>("/api/settings/default-prep-time", {
        method: "PUT",
        body: JSON.stringify({ defaultPrepTime }),
      });
    },
    /**
     * Les charges mensuelles qui entrent dans le cout d'une commande :
     * IA, telephonie, hebergement, abonnements. Les salaires des
     * responsables restent regles magasin par magasin.
     */
    /* L'interrupteur de la quarantaine. Suspendue, la synchronisation ne
       marque plus aucun article disparu et la purge n'efface plus rien —
       le temps qu'un catalogue Glovo abime redevienne fiable. */
    getQuarantaine: async () =>
      apiRequest<{ suspendue: boolean; majLe: string | null }>(
        "/api/settings/quarantaine",
      ),

    setQuarantaine: async (suspendue: boolean) =>
      apiRequest<{ success: boolean; suspendue: boolean }>(
        "/api/settings/quarantaine",
        { method: "PUT", body: JSON.stringify({ suspendue }) },
      ),

    getCharges: async () =>
      apiRequest<{
        charges: Array<{
          libelle: string;
          montantCentimes: number;
          categorie: string;
          actif: boolean;
        }>;
        categories: string[];
      }>("/api/settings/charges"),
    updateCharges: async (
      charges: Array<{
        libelle: string;
        montantCentimes: number;
        categorie: string;
        actif: boolean;
      }>,
    ) =>
      apiRequest<{ success: boolean }>("/api/settings/charges", {
        method: "PUT",
        body: JSON.stringify({ charges }),
      }),
  },

  // ============ IMPORT ALL PRODUCT ============
  images: {
    /** Le fichier SKU / nom / photo d'un magasin. */
    telecharger: async (storeId: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Session expirée, reconnecte-toi.");

      const reponse = await fetch(
        `${API_BASE_URL}/api/stores/${storeId}/images`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!reponse.ok) {
        throw new Error((await reponse.text()) || "Export impossible.");
      }

      const blob = await reponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `images-${storeId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  },

  comptabilite: {
    /**
     * Le classeur d'une quinzaine. Le navigateur telecharge le fichier :
     * on ne passe pas par apiRequest, qui attend du JSON.
     */
    telecharger: async (
      storeId: string,
      annee: number,
      mois: number,
      quinzaine: 1 | 2,
    ) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Session expirée, reconnecte-toi.");

      const reponse = await fetch(
        `${API_BASE_URL}/api/stores/${storeId}/comptabilite` +
          `?annee=${annee}&mois=${mois}&quinzaine=${quinzaine}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!reponse.ok) {
        throw new Error((await reponse.text()) || "Export impossible.");
      }

      const blob = await reponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compta-${annee}-${String(mois).padStart(2, "0")}-Q${quinzaine}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },

    /**
     * Le meme classeur, mais pour UNE journee (AAAA-MM-JJ) : les prix qui
     * faisaient foi ce jour-la.
     */
    telechargerJour: async (storeId: string, jour: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Session expirée, reconnecte-toi.");

      const reponse = await fetch(
        `${API_BASE_URL}/api/stores/${storeId}/comptabilite?jour=${encodeURIComponent(jour)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!reponse.ok) {
        throw new Error((await reponse.text()) || "Export impossible.");
      }

      const blob = await reponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compta-${jour}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  },

  allProduct: {
    /**
     * Envoie le fichier au serveur. `simulation` a true : il compte et
     * rapporte sans rien ecrire.
     */
    importer: async (
      storeId: string,
      fichier: File,
      simulation: boolean,
      feuille?: string,
      /** Lequel des deux fichiers. Ne sert qu'a la trace. */
      type: "CLASSIQUE" | "FL" | "JOUR" | "VIANDE" = "CLASSIQUE",
    ) => {
      const base64: string = await new Promise((resolve, reject) => {
        const lecteur = new FileReader();
        lecteur.onload = () => {
          const r = String(lecteur.result || "");
          resolve(r.slice(r.indexOf(",") + 1)); // retirer « data:...;base64, »
        };
        lecteur.onerror = () => reject(new Error("Lecture du fichier impossible."));
        lecteur.readAsDataURL(fichier);
      });

      return apiRequest<{
        simulation: boolean;
        feuille: string | null;
        recus: number;
        appliques: number;
        introuvables: string[];
        incomplets: string[];
        fournisseursInconnus: string[];
        contenu: Array<{ categorie: string; nombre: number }>;
        exemples: string[];
        prixDuFichier: number;
        prixRecalcules: number;
        /**
         * Combien de prix site et Kooul ce fichier portait vraiment.
         *
         * Optionnels : le serveur qui les renvoie peut etre plus ancien que
         * la page qui les lit — c'est le cas entre deux deploiements, le
         * back et le front ne partant pas ensemble. L'ecran doit alors se
         * taire, pas afficher « 0 prix site » et faire croire a un fichier
         * vide.
         */
        prixSite?: number;
        prixKooul?: number;
        ecartsGlovo: Array<{ sku: string; calcule: number; glovo: number }>;
        affectations: Array<{
          fournisseur: string;
          ajoutees: string[];
          deja: number;
        }>;
      }>(`/api/stores/${storeId}/all-product`, {
        method: "POST",
        body: JSON.stringify({
          fichierBase64: base64,
          simulation,
          feuille,
          type,
        }),
      });
    },
  },

  /**
   * LA CAISSE DU RESPONSABLE COMMANDE.
   *
   * Ce qu'on lui a confie, ce qu'il a depense, et le solde qui court.
   * Les montants sont en CENTIMES : l'ecran divise par cent au moment
   * d'afficher, et a un seul endroit.
   */
  /* ======================================================================
   *  COMPTABILITE ANALYTIQUE — une ligne par article vendu
   * ==================================================================== */
  analytique: {
    /** Les lignes et les totaux, pour l'ecran. Montants en DIRHAMS. */
    lire: (p: {
      storeId: string;
      annee?: number;
      mois?: number;
      quinzaine?: 1 | 2;
      /** AAAA-MM-JJ. Prend le pas sur le trio annee/mois/quinzaine. */
      jour?: string;
    }) => {
      const q = new URLSearchParams({ storeId: p.storeId });
      if (p.jour) q.append("jour", p.jour);
      else {
        if (p.annee) q.append("annee", String(p.annee));
        if (p.mois) q.append("mois", String(p.mois));
        if (p.quinzaine) q.append("quinzaine", String(p.quinzaine));
      }
      return apiRequest<{
        periode: { debut: string; fin: string };
        lignes: Array<Record<string, any>>;
        totaux: {
          lignes: number;
          caGlobal: number;
          coutAchat: number;
          coutEmballage: number;
          livraison: number;
          remboursement: number;
          margeGlobale: number;
          sansRattachement: number;
          prixAchatManquant: number;
          livraisonProvisionnee: number;
        };
      }>(`/api/accounting/analytique?${q.toString()}`);
    },

    /**
     * Le meme tableau, en classeur.
     *
     * Il passe par ici et non par un lien direct : la route demande un
     * jeton, et une adresse collee dans un navigateur n'en porte aucun.
     */
    telecharger: async (p: {
      storeId: string;
      annee?: number;
      mois?: number;
      quinzaine?: 1 | 2;
      jour?: string;
    }) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Non authentifié");

      const q = new URLSearchParams({ storeId: p.storeId });
      if (p.jour) q.append("jour", p.jour);
      else {
        if (p.annee) q.append("annee", String(p.annee));
        if (p.mois) q.append("mois", String(p.mois));
        if (p.quinzaine) q.append("quinzaine", String(p.quinzaine));
      }

      q.append("_", String(Date.now())); // toujours l'etat du moment (02/11/2026)
      const reponse = await fetch(
        `${API_BASE_URL}/api/accounting/analytique.xlsx?${q.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" },
      );
      if (!reponse.ok) {
        let message = "Export impossible";
        try {
          message = (await reponse.json())?.message || message;
        } catch {
          /* la reponse n'etait pas du JSON */
        }
        throw new Error(message);
      }

      const nom =
        /filename="([^"]+)"/.exec(
          reponse.headers.get("Content-Disposition") || "",
        )?.[1] || "analytique.xlsx";

      const blob = await reponse.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      document.body.removeChild(lien);
      URL.revokeObjectURL(url);
      return nom;
    },

    /** Le suivi d'une quinzaine : synthese, facture Glovo, paiements (03/11/2026). */
    relevesPeriodes: () =>
      apiRequest<{ periodes: Array<{ cle: string; magasin: string; annee: number; mois: number; quinzaine: number }> }>(
        "/api/accounting/releves-periodes",
      ),
    relevePeriode: (cle: string) => apiRequest<any>(`/api/accounting/releves-periodes/${encodeURIComponent(cle)}`),
    /** Les photos des remboursements d'un frs, pour son releve (04/11/2026). */
    photosDuReleve: (cle: string, id: string) =>
      apiRequest<{
        photos: Array<{ numero: string; date: string; article: string | null; motif: string | null; photo: string }>;
      }>(`/api/accounting/releves-periodes/${encodeURIComponent(cle)}/releves/${encodeURIComponent(id)}/photos`),
    /** Les releves PDF gardes d'une quinzaine (03/11/2026). */
    relevesDeLaPeriode: (cle: string) =>
      apiRequest<{
        fournisseurs: Array<{
          fournisseur: string;
          nomFichier: string;
          totalCentimes: number;
          lignes: Array<{ date: string; numero: string; element: string; quantite: number; coutCentimes: number }>;
        }>;
      }>(`/api/accounting/releves-periodes/${encodeURIComponent(cle)}/releves`),
    enregistrerRelevePeriode: (
      cle: string,
      corps: {
        magasin?: string;
        synthese?: any;
        facture?: any;
        suivi?: any;
        tauxKooulPct?: number;
        cloturee?: boolean;
      },
    ) =>
      apiRequest<any>(`/api/accounting/releves-periodes/${encodeURIComponent(cle)}`, {
        method: "PUT",
        body: JSON.stringify(corps),
      }),
    /** Les releves des fournisseurs, lus dans l'export « Commandes » (02/11/2026). */
    relevesFournisseurs: async (fichier: File) => {
      const base64: string = await new Promise((resolve, reject) => {
        const lecteur = new FileReader();
        lecteur.onload = () => {
          const r = String(lecteur.result || "");
          resolve(r.slice(r.indexOf(",") + 1));
        };
        lecteur.onerror = () => reject(new Error("Lecture du fichier impossible."));
        lecteur.readAsDataURL(fichier);
      });
      return apiRequest<{
        magasin: string;
        annee: number;
        mois: number;
        moisCourt: string;
        quinzaine: number;
        lignesIgnorees: number;
        fournisseurs: Array<{
          fournisseur: string;
          nomFichier: string;
          totalCentimes: number;
          lignes: Array<{ date: string; numero: string; element: string; quantite: number; coutCentimes: number }>;
        }>;
        cle: string;
        synthese: {
          lignes: LigneSyntheseReleve[];
          caTotalCentimes: number;
          caGlovoCentimes: number;
          remboursementGlovoCentimes: number;
          caParCanal: Partial<Record<CanalReleve, number>>;
          livraisonsARenseigner: string[];
        };
        /** La quinzaine est cloturee : rien n'a ete enregistre. */
        cloturee?: boolean;
      }>("/api/accounting/releves-fournisseurs", {
        method: "POST",
        body: JSON.stringify({ fichierBase64: base64 }),
      });
    },

    /**
     * L'export de la page Commandes : une ligne par article, avec les
     * filtres de la page (periode, magasin, plateforme, statut).
     * Reserve a l'admin — la route le verifie de son cote.
     */
    exporterCommandes: async (p: {
      dateFilter: string;
      storeId?: string;
      source?: string;
      status?: string;
    }) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Non authentifié");

      const q = new URLSearchParams({ dateFilter: p.dateFilter });
      if (p.storeId && p.storeId !== "ALL") q.append("storeId", p.storeId);
      if (p.source && p.source !== "ALL") q.append("source", p.source);
      if (p.status && p.status !== "ALL") q.append("status", p.status);

      // JAMAIS DE FICHIER GARDE EN MEMOIRE (02/11/2026) : apres une
      // correction (article retire, prix modifie), l'export doit sortir
      // l'etat du moment, pas celui de l'extraction precedente.
      q.append("_", String(Date.now()));
      const reponse = await fetch(
        `${API_BASE_URL}/api/accounting/commandes.xlsx?${q.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" },
      );
      if (!reponse.ok) {
        let message = "Export impossible";
        try {
          message = (await reponse.json())?.message || message;
        } catch {
          /* la reponse n'etait pas du JSON */
        }
        throw new Error(message);
      }

      const nom =
        /filename="([^"]+)"/.exec(
          reponse.headers.get("Content-Disposition") || "",
        )?.[1] || "commandes.xlsx";

      const blob = await reponse.blob();
      const url = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      document.body.removeChild(lien);
      URL.revokeObjectURL(url);
      return nom;
    },
  },

  caisse: {
    /** Le tableau de l'ecran, en fichier Excel (meme personne, meme quinzaine). */
    telecharger: async (responsableId?: string, periode?: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Non authentifié");
      const p = new URLSearchParams({ format: "xlsx" });
      if (responsableId) p.append("responsableId", responsableId);
      if (periode) p.append("periode", periode);
      const reponse = await fetch(`${API_BASE_URL}/api/caisse?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!reponse.ok) {
        let message = "Téléchargement impossible";
        try {
          message = (await reponse.json())?.message || message;
        } catch {
          /* pas du JSON */
        }
        throw new Error(message);
      }
      const nom =
        /filename="([^"]+)"/.exec(reponse.headers.get("Content-Disposition") || "")?.[1] ||
        "caisse.xlsx";
      const url = URL.createObjectURL(await reponse.blob());
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = nom;
      document.body.appendChild(lien);
      lien.click();
      document.body.removeChild(lien);
      URL.revokeObjectURL(url);
      return nom;
    },

    lire: (responsableId?: string, periode?: string) => {
      const p = new URLSearchParams();
      if (responsableId) p.append("responsableId", responsableId);
      if (periode) p.append("periode", periode);
      const q = p.toString();
      return apiRequest<{
        periode: { debut: string; fin: string };
        responsableId: string | null;
        responsables: Array<{ id: string; nom: string }>;
        ouvertureCentimes: number;
        soldeCentimes: number;
        sortiesCentimes: number;
        entreesCentimes: number;
        depassements: number;
        sansDate: number;
        /** La quinzaine est cloturee (01/11/2026) : seul l'admin la modifie. */
        cloturee?: boolean;
        lignes: Array<{
          id: string;
          genre: "ACHAT" | "ALIMENTATION" | "DEPART";
          le: string;
          nom: string;
          orderCode: string | null;
          /** Heure d'arrivee de la commande (ISO), sous son numero. */
          heureCommande?: string | null;
          article: string;
          quantite: number;
          sortieCentimes: number;
          entreeCentimes: number;
          prixUnitaireCentimes: number | null;
          prixAchatCentimes: number | null;
          ecartCentimes: number | null;
          note: string | null;
          /**
           * La derniere colonne de la feuille : a quoi rattacher la charge
           * quand ce n'est pas Glovo — « site », ou une depense
           * extraordinaire. Null = charge ordinaire.
           */
          compta: string | null;
          soldeCentimes: number;
        }>;
      }>(`/api/caisse${q ? `?${q}` : ""}`);
    },

    /** Admin : cloture (sauvegarde) ou rouvre une quinzaine de caisse (01/11/2026). */
    cloturer: (periode: string, cloturee: boolean) =>
      apiRequest<{ success: boolean; cle: string; cloturee: boolean; personnes: number }>(
        "/api/caisse/cloture",
        { method: "POST", body: JSON.stringify({ periode, cloturee }) },
      ),

    /** Admin : deplace l'historique d'une caisse (lignes avant `avant`) vers une autre personne. */
    transferer: (data: { de: string; vers: string; avant: string }) =>
      apiRequest<{ success: boolean; achats: number; remises: number }>(
        "/api/caisse/transferer",
        { method: "POST", body: JSON.stringify(data) },
      ),

    /** L'argent remis dans la caisse. Montant en DIRHAMS, negatif admis. */
    alimenter: (data: {
      responsableId: string;
      montant: number;
      le?: string;
      libelle?: string;
      note?: string;
      depart?: boolean;
    }) =>
      apiRequest<{ success: boolean; id: string; par: string }>(
        "/api/caisse/alimentation",
        { method: "POST", body: JSON.stringify(data) },
      ),

    /** Un achat caisse saisi a la main. Prix en DIRHAMS, unitaires. */
    ajouterAchat: (data: {
      responsableId?: string;
      orderCode?: string;
      article: string;
      quantite: number;
      prixPaye: number;
      prixAchat?: number;
      le?: string;
      /** « site », « recharge »… Vide pour une charge Glovo ordinaire. */
      compta?: string;
      /** Le total sorti de la caisse. Il l'emporte sur prixPaye × quantite. */
      cout?: number;
    }) =>
      apiRequest<{ success: boolean; id: string }>("/api/caisse/achat", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /**
     * CORRIGER UNE LIGNE DEJA ENREGISTREE. Direction seulement.
     *
     * Montants en DIRHAMS. Seuls les champs envoyes sont touches ; la
     * valeur d'avant est conservee dans la ligne, avec la date et le nom.
     */
    corrigerLigne: (
      id: string,
      data: {
        genre: "ACHAT" | "ALIMENTATION" | "DEPART";
        article?: string;
        quantite?: number;
        prixPaye?: number;
        prixAchat?: number;
        cout?: number | null;
        compta?: string;
        orderCode?: string;
        libelle?: string;
        montant?: number;
        note?: string;
        le?: string;
      },
    ) =>
      apiRequest<{ success: boolean; corrigePar?: string; inchange?: boolean }>(
        `/api/caisse/ligne/${id}`,
        { method: "PUT", body: JSON.stringify(data) },
      ),

    /**
     * ECARTER UNE LIGNE ENTREE PAR ERREUR. Direction seulement.
     * Elle sort du compte et de l'ecran ; elle reste en base.
     */
    ecarterLigne: (
      id: string,
      genre: "ACHAT" | "ALIMENTATION" | "DEPART",
      motif?: string,
    ) =>
      apiRequest<{ success: boolean; ecarteePar?: string }>(
        `/api/caisse/ligne/${id}?genre=${genre}`,
        { method: "DELETE", body: JSON.stringify({ motif: motif || "" }) },
      ),

    /**
     * La feuille du responsable, qui fait loi. Chaque ligne porte une cle
     * stable : reimporter remplace, ne double jamais. `retirerTests` ecarte
     * du solde (sans les effacer) les achats qui ne viennent pas d'elle.
     */
    importerFeuille: (data: {
      responsableId: string;
      retirerTests?: boolean;
      lignes: unknown[];
    }) =>
      apiRequest<{ success: boolean; importees: number; ecartees: number }>(
        "/api/caisse/import",
        { method: "POST", body: JSON.stringify(data) },
      ),
  },

  // ============ IMPORT DE L'EXPORT GLOVO ============
  // Le fichier part en texte brut : un export d'une journée dépasse déjà la
  // limite JSON par défaut, et l'échapper en chaîne JSON n'apporterait rien.
  importGlovo: {
    /** Les retenues du magasin, avec leurs photos (responsable commande). */
    remboursementsAvecPhotos: (storeId?: string) =>
      apiRequest<{
        total: number;
        sansPhoto: number;
        /** Debut de la periode comptable couverte (1er ou 16 du mois). */
        periodeDebut?: string;
        lignes: Array<{
          id: string;
          numero: string;
          montantCentimes: number;
          motif: string | null;
          le: string | null;
          storeId: string | null;
          qualifications: Array<{
            articleId: string;
            article: string;
            motif: string;
            /** Nombre d'unites remboursees (1 pour les lignes anciennes). */
            quantite: number;
          }>;
          photos: Array<{ id: string; photo: string; article: string | null; motif: string | null }>;
        }>;
      }>(
        `/api/import-glovo/remboursements-avec-photos${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`,
      ),

    essai: async (csv: string) => {
      return apiRequest<RapportImportGlovo>("/api/import-glovo/essai", {
        method: "POST",
        headers: { "Content-Type": "text/csv" },
        body: csv,
      });
    },
    appliquer: async (csv: string) => {
      return apiRequest<ResultatImportGlovo>("/api/import-glovo/appliquer", {
        method: "POST",
        headers: { "Content-Type": "text/csv" },
        body: csv,
      });
    },
    journal: async (nombre = 20) => {
      return apiRequest<{ total: number; lignes: LigneJournalGlovo[] }>(
        `/api/import-glovo/journal?nombre=${nombre}`,
      );
    },
    /** Le detail d'un import : les commandes qui font l'ecart. */
    detailImport: async (id: string) => {
      return apiRequest<DetailImportGlovo>(`/api/import-glovo/journal/${encodeURIComponent(id)}`);
    },

    /**
     * Une commande livree chez Glovo et perdue ici. Sans `ecrire` : apercu,
     * rien n'est ecrit. Avec : recreee depuis le fichier (direction seule).
     */
    /** La direction a vu la commande et ne la recree pas. */
    marquerAbsenteVue: async (numero: string, note: string) =>
      apiRequest<{ ok: boolean }>(
        `/api/import-glovo/absente/${encodeURIComponent(numero)}/vu`,
        { method: "POST", body: JSON.stringify({ note }) },
      ),

    /** Le controle automatique tourne-t-il vraiment ? */
    santeGardien: async () =>
      apiRequest<{
        etat: "OK" | "PARTIEL" | "EN_PANNE" | "EN_RETARD" | "ARRETE" | "JAMAIS" | "AVEUGLE";
        mode: string;
        dernierPassage: string | null;
        ilYAMinutes: number | null;
        commandesVues: number;
        absentes: number;
        erreurs: { ou: string; message: string }[];
        /** Magasins pour lesquels Glovo ne renvoie rien alors que l'appli a recu des commandes. */
        aveugles?: { nom: string; recuesIci: number }[];
        passagesEnErreur24h: number;
        passages24h: number;
      }>("/api/import-glovo/reconciliation/sante"),

    commandeAbsente: async (csv: string, numero: string, ecrire: boolean) =>
      apiRequest<ApercuCommandeAbsente>(
        `/api/import-glovo/absente/${encodeURIComponent(numero)}${ecrire ? "?ecrire=1" : ""}`,
        { method: "POST", headers: { "Content-Type": "text/csv" }, body: csv },
      ),

    /** Les commandes remboursees par Glovo, et l'etat de leurs preuves. */
    remboursements: async (nombre = 50) =>
      apiRequest<{
        total: number;
        aJustifier: number;
        lignes: LigneRemboursement[];
      }>(`/api/import-glovo/remboursements?nombre=${nombre}`),

    /**
     * Joindre une photo. Elle part en text/plain, pas en JSON : l'analyseur
     * JSON du serveur plafonne a 100 Ko, une photo le depasserait.
     */
    joindreJustificatif: async (
      orderId: string,
      photo: string,
      motif: string,
      article: string,
      articleId: string,
      quantite = 1,
    ) =>
      apiRequest<{ success: boolean; nbJustificatifs: number }>(
        `/api/import-glovo/remboursements/${orderId}/justificatif` +
          `?motif=${encodeURIComponent(motif)}` +
          `&article=${encodeURIComponent(article)}` +
          `&articleId=${encodeURIComponent(articleId)}` +
          `&quantite=${encodeURIComponent(String(quantite))}`,
        { method: "POST", headers: { "Content-Type": "text/plain" }, body: photo },
      ),

    /**
     * Le motif d'un article, sans photo. Indispensable quand la preuve n'est
     * pas de notre cote : sur une mauvaise commande, c'est le client qui
     * photographie et Glovo qui garde l'image.
     */
    /** Ferme la ligne : tous les articles rembourses sont qualifies (24/10/2026). */
    validerRemboursement: (orderId: string) =>
      apiRequest<{ success: boolean; deja?: boolean }>(
        `/api/import-glovo/remboursements/${orderId}/valider`,
        { method: "PUT" },
      ),
    /** L'admin rouvre une ligne validee. */
    /** Les dernieres quinzaines et leur cloture (01/11/2026). */
    cloturesRemboursements: () =>
      apiRequest<{
        periodes: Array<{ cle: string; libelle: string; cloturee: boolean; parNom: string | null; le: string | null }>;
      }>("/api/import-glovo/remboursements/clotures"),
    cloturerPeriode: (cle: string, cloturee: boolean) =>
      apiRequest<{ success: boolean }>(
        `/api/import-glovo/remboursements/clotures/${encodeURIComponent(cle)}`,
        { method: "PUT", body: JSON.stringify({ cloturee }) },
      ),
    rouvrirRemboursement: (orderId: string) =>
      apiRequest<{ success: boolean }>(
        `/api/import-glovo/remboursements/${orderId}/rouvrir`,
        { method: "PUT" },
      ),
    enregistrerMotif: async (
      orderId: string,
      articleId: string,
      article: string,
      motif: string,
      /** Combien d'unites sont remboursees. Le serveur plafonne au commande. */
      quantite = 1,
    ) =>
      apiRequest<{
        success: boolean;
        motif: string | null;
        imputation: "MAGASIN" | "FOURNISSEUR" | null;
      }>(`/api/import-glovo/remboursements/${orderId}/motif`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ articleId, article, motif, quantite }),
      }),

    /**
     * Retirer un article impute par erreur — avec SES photos.
     *
     * Laisser les photos derriere ferait mentir le compteur « sans
     * preuve » : la ligne passerait pour justifiee par une preuve qui ne la
     * concerne plus.
     */
    retirerArticle: async (orderId: string, articleId: string) =>
      apiRequest<{
        success: boolean;
        retire: boolean;
        photosSupprimees: number;
        nbJustificatifs: number;
      }>(
        `/api/import-glovo/remboursements/${orderId}/article/${encodeURIComponent(articleId)}`,
        { method: "DELETE" },
      ),

    justificatifs: async (orderId: string) =>
      apiRequest<{ total: number; photos: JustificatifPhoto[] }>(
        `/api/import-glovo/remboursements/${orderId}/justificatifs`,
      ),
  },

  /**
   * Commandes du site web (Wix).
   *
   * Trois modes, du plus prudent au plus engageant :
   *   arrete : on ignore tout ce qui arrive ;
   *   test   : on lit et on calcule le magasin, sans creer la commande ;
   *   actif  : la commande est creee dans le magasin le plus proche.
   */
  commandesSite: {
    /** Le mode en cours et l'expediteur surveille. */
    etat: () =>
      apiRequest<{
        mode: "arrete" | "test" | "actif";
        expediteurSurveille: string;
        explication: string;
      }>(`/api/commandes-site/etat`),
    /** Relit les derniers mails du site. N'ecrit rien, jamais. */
    essai: (nombre = 3) =>
      apiRequest<any>(`/api/commandes-site/essai?nombre=${nombre}`),
    /** Ce que le lecteur a fait ces derniers jours. */
    journal: () => apiRequest<any>(`/api/commandes-site/journal`),
    /** Changer de mode (droit de reglage requis). */
    changerMode: (mode: "arrete" | "test" | "actif") =>
      apiRequest<{ mode: string; message: string }>(
        `/api/commandes-site/mode`,
        { method: "POST", body: JSON.stringify({ mode }) },
      ),
  },

  // Notifications
  notifications: {
    sendTest: async (data: { role: string; title: string; body: string }) => {
      // ✅ Utilise la fonction séparée notificationsApi
      const notifApiUrl = import.meta.env.VITE_NOTIFICATIONS_API_URL || 
        "https://europe-west1-natura-beldi-glovo-management.cloudfunctions.net/notificationsApi";
      
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(`${notifApiUrl}/api/notifications/send-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to send notification");
      }

      return response.json();
    },
  },

  /** Les liens de prix des fournisseurs (27/10/2026). Voir liens-tarifs.handler. */
  liensTarifs: {
    lister: () =>
      apiRequest<{
        liens: Array<{
          jeton: string;
          magasin: string;
          rayon: string;
          fournisseurNom: string | null;
          nomSaisi: string | null;
          actif: boolean;
          nbProduits: number;
          nbPrix: number;
          nbDispo: number;
          nbPasDispo: number;
          creeLe: string | null;
          envoyeLe: string | null;
          nbEnvois: number;
        }>;
      }>("/api/liens-tarifs"),
    lireCatalogue: (fichierBase64: string) =>
      apiRequest<{ produits: Array<{ sku: string; nom: string; categorie: string; actif: boolean }> }>(
        "/api/liens-tarifs/lire-catalogue",
        { method: "POST", body: JSON.stringify({ fichierBase64 }) },
      ),
    creer: (donnees: {
      magasin: string;
      rayon: string;
      fournisseurNom?: string;
      produits: Array<{ sku: string; nom: string; categorie: string }>;
    }) =>
      apiRequest<{ ok: boolean; jeton: string; nbProduits: number }>("/api/liens-tarifs", {
        method: "POST",
        body: JSON.stringify(donnees),
      }),
    modifier: (jeton: string, maj: { actif?: boolean; fournisseurNom?: string }) =>
      apiRequest<{ ok: boolean }>(`/api/liens-tarifs/${jeton}`, { method: "PUT", body: JSON.stringify(maj) }),
    telecharger: async (jeton: string, nomFichier: string) => {
      const token = await getAuthToken();
      if (!token) throw new Error("Session expirée, reconnecte-toi.");
      const reponse = await fetch(`${API_BASE_URL}/api/liens-tarifs/${jeton}/excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!reponse.ok) throw new Error((await reponse.text()) || "Téléchargement impossible.");
      const blob = await reponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nomFichier.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  },
};

export default api;

/** Une ligne de la synthese des fournisseurs d'une quinzaine (03/11/2026). */
export interface LigneSyntheseReleve {
  fournisseur: string;
  caisse: boolean;
  caCentimes: number;
  coutCentimes: number;
  emballageCentimes: number;
  remboursementCentimes: number;
  /** La part des courses payees aux livreurs (site, ou livreur en plus). */
  livreurCentimes?: number;
  /** Les memes chiffres, canal par canal (03/11/2026). */
  parCanal?: Partial<Record<CanalReleve, ChiffresCanalReleve>>;
  raison: string;
  caPourcent: number;
  lignes: number;
}

export type CanalReleve = "GLOVO" | "SITE" | "KOOUL";

export interface ChiffresCanalReleve {
  caCentimes: number;
  coutCentimes: number;
  emballageCentimes: number;
  remboursementCentimes: number;
  livreurCentimes: number;
}
